import { confirmDialog } from '../components/ui/ConfirmDialog';
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import { useNavigate } from 'react-router-dom';
import { Mic, Sparkles, Plus, Save, ArrowLeft, Loader2, FileText, Tag, MapPin, User, Check, Globe, Network, Camera, Trash2, UploadCloud, Video, Paperclip, Power, Search, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { extractPropertyDetails, enhanceDescription, AIError } from '../services/aiService';
import { Property } from '../types/types';
import { clsx } from 'clsx';
import { optimizeImage, isAcceptableImage, isHeavyImage } from '../lib/optimize-image';
import { htmlNotesToMarkdown } from '../lib/html-notes';
import { supabase } from '../lib/supabase';
import { reportError, coerceError } from '../lib/report-error';
import { uploadImageToR2 } from '../lib/upload-r2';
import { validatePropertyDoc } from '../lib/property-documents';
import { smartTitleCase } from '../lib/title-format';
import { isAmenitySelected, toggleAmenity } from '../lib/amenity-slug';
import { useGtqRate, formatUsd, formatGtq } from '../lib/currency';
import { useAuth } from '../contexts/AuthContext';
import { useAIQuota } from '../hooks/useAIQuota';
import { LocationMap, type MapFocus } from '../components/ui/LocationMap';
import { PHOTO_LABEL_PRESETS } from '../lib/photo-labels';
import { MarkdownEditor } from '../components/ui/MarkdownEditor';
import { videoLinkProvider, VIDEO_LINK_LABEL, VIDEO_LINK_PLACEHOLDER, VIDEO_LINK_HELP, VIDEO_LINK_INVALID } from '../lib/video-link';

interface AddPropertyViewProps {
    theme: 'light' | 'dark' | 'high-contrast';
    onSave: (property: Property) => void | Promise<void>;
    onCancel: () => void;
    initialData?: Property;
}

// Convert (HEIC→JPEG when needed) + resize + WebP-encode a file for upload.
// Delegates to the shared optimizeImage helper so the gallery, avatars, and
// logos all decode the same way — crucially including iPhone HEIC, which most
// browsers can't decode through <canvas>.
// Gallery photos: 3000px long edge, WebP quality 0.92 — a high-quality master.
// The Cloudflare image Worker downscales it for public portal delivery, so the
// extra resolution/quality mainly benefits the CRM full-size detail view.
const optimizeImageToBlob = (file: File, maxDimension: number = 3000): Promise<Blob> => {
    return optimizeImage(file, { maxDimension, quality: 0.92, fileType: 'image/webp' });
};

// Upload an optimized blob to R2 `images` bucket under covers/{agency_id}/gallery/
// Mirrors mobile's pickAndUploadMultipleImages convention.
// When `generateOgThumb` is true (only for the cover photo of a fresh upload),
// the helper also produces an 800×420 JPEG OG thumbnail for fast WhatsApp/FB
// link previews via the vitrina-preview edge function.
const uploadGalleryBlob = async (
    blob: Blob,
    agencyId: string,
    idx: number,
    generateOgThumb: boolean,
): Promise<{ url: string; ogThumbUrl?: string }> => {
    const result = await uploadImageToR2(blob, {
        folder: 'covers',
        subfolder: `${agencyId}/gallery`,
        filename: `${Date.now()}-${idx}.webp`,
        generateOgThumb,
    });
    return { url: result.url, ogThumbUrl: result.ogThumbUrl };
};

// Hardcoded fallback coordinates for Guatemala departments (used when DB has no coords yet)
const DEPT_COORDS: Record<string, [number, number]> = {
    '*Guatemala, Ciudad': [14.6349, -90.5069],
    '*Internacional': [14.6349, -90.5069],
    'Sacatepéquez': [14.5586, -90.7295],
    'Chimaltenango': [14.6347, -90.8186],
    'Quetzaltenango': [14.8333, -91.5167],
    'Sololá': [14.8448, -91.1845],
    'Totonicapán': [14.7756, -91.1833],
    'Huehuetenango': [15.3167, -91.4667],
    'San Marcos': [14.9667, -91.7833],
    'Alta Verapaz': [15.4833, -90.3667],
    'Baja Verapaz': [15.1203, -90.3774],
    'Petén': [16.9167, -89.8833],
    'Izabal': [15.7347, -88.5964],
    'Zacapa': [14.9500, -89.5333],
    'Chiquimula': [14.6333, -89.6333],
    'Jalapa': [14.5667, -89.9833],
    'Jutiapa': [14.2833, -90.0167],
    'Santa Rosa': [14.2779, -90.2747],
    'Escuintla': [14.2860, -90.7937],
    'Suchitepéquez': [14.5333, -91.5000],
    'Retalhuleu': [14.5333, -91.6167],
    'El Progreso': [14.8500, -90.0667],
    'Quiché': [15.0333, -91.1500],
};

// Land area is stored in varas² (lot_size_varas); the m² box is a convenience
// input that fills the varas box and vice-versa. 1 vara = 0.8359 m (vara
// centroamericana) → 1 vara² = 0.69873 m². Keep this factor in sync with the
// mobile form (mobile/app/(app)/(tabs)/propiedades/nueva.tsx).
const M2_PER_VARA2 = 0.8359 * 0.8359;
const round2 = (n: number) => Math.round(n * 100) / 100;

// ── AI prefill helpers ───────────────────────────────────────
// Normalize a name for fuzzy lookup matching: lowercase, strip accents (NFD)
// and the legacy sort-pin asterisk prefix (e.g. "*Guatemala, Ciudad").
const normalize = (s: string): string =>
    s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/^\*+/, '').trim();

// Extract the N from "zona N" style names ("Zona 10" → 10); null when absent.
const zoneNumber = (s: string): number | null => {
    const m = s.match(/zona\s*(\d+)/);
    return m ? Number(m[1]) : null;
};

// Best-effort lookup match: normalized equality first, then prefix.
const findByName = <T extends { id: number; name: string }>(list: T[], raw?: string): T | undefined => {
    if (!raw) return undefined;
    const q = normalize(raw);
    if (!q) return undefined;
    return list.find(i => normalize(i.name) === q) ?? list.find(i => normalize(i.name).startsWith(q));
};

// Shared Spanish alert mapping for AI errors (project convention: alert()).
const alertAIError = async (e: unknown, onActivate?: () => void) => {
    if (e instanceof AIError) {
        if (e.code === 'AI_NO_SUB') {
            if (onActivate && await confirmDialog('No tienes una suscripción de IA activa. ¿Quieres activarla ahora?', { confirmLabel: 'Activar' })) onActivate();
            else if (!onActivate) alert('No tienes una suscripción de IA activa. Actívala desde Configuración.');
        }
        else if (e.code === 'AI_QUOTA_EXCEEDED') alert('Has alcanzado el límite de tu plan de IA este mes.');
        else if (e.code === 'AI_DAILY_LIMIT') alert('Llegaste al límite de uso de hoy. Mañana a primera hora puedes seguir.');
        else if (e.code === 'AI_RATE_LIMITED') alert('Demasiadas solicitudes. Espera un momento.');
        else if (e.code === 'AI_AUTH_ERROR') alert('Tu sesión expiró. Vuelve a iniciar sesión.');
        else alert('El asistente no está disponible en este momento. Intenta de nuevo.');
    } else {
        console.error(e);
        alert('El asistente no está disponible en este momento. Intenta de nuevo.');
    }
};

export const AddPropertyView: React.FC<AddPropertyViewProps> = ({ theme, onSave, onCancel, initialData }) => {
    const { profile, realProfile } = useAuth();
    const navigate = useNavigate();
    const aiQuota = useAIQuota();
    const [inputText, setInputText] = useState('');
    const [isRecording, setIsRecording] = useState(false);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    // Post-fill feedback line for the AI panel («Campos completados: …»).
    const [aiFillSummary, setAiFillSummary] = useState<{ ok: boolean; text: string } | null>(null);
    // «Mejorar con IA» on Descripción — previous value kept for a one-step undo.
    const [isEnhancing, setIsEnhancing] = useState(false);
    const [canUndoEnhance, setCanUndoEnhance] = useState(false);
    const prevDescriptionRef = useRef<string | null>(null);

    // DB state for dropdowns
    const [departments, setDepartments] = useState<any[]>([]);
    const [sectors, setSectors] = useState<any[]>([]);
    const [cities, setCities] = useState<any[]>([]);
    const [agents, setAgents] = useState<any[]>([]);
    const [availableAmenities, setAvailableAmenities] = useState<any[]>([]);
    const [propertyTypes, setPropertyTypes] = useState<any[]>([]);
    const [priceTags, setPriceTags] = useState<any[]>([]);
    // Global marketing tags (property_tags) — multi-select, saved to
    // property_tag_assignments by the parent onSave handler via tag_ids.
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
    const [owners, setOwners] = useState<any[]>([]);
    // Owner combobox: the agency-wide owner list got too long for a <select>,
    // so the picker is a type-to-search input filtering the loaded list.
    const [ownerSearch, setOwnerSearch] = useState('');
    const [ownerDropdownOpen, setOwnerDropdownOpen] = useState(false);
    const [resolvedOwnerName, setResolvedOwnerName] = useState('');
    const [proyectos, setProyectos] = useState<{ id: string; nombre: string }[]>([]);

    // Toggles state. Editing reflects the saved row; a NEW property starts from
    // the user's defaults (Ajustes de usuario → Nuevos registros), which fall
    // back to publish-on / share-off — the behavior before the pref existed.
    const [publishStatus, setPublishStatus] = useState<'published' | 'unpublished'>(
        initialData
            ? (initialData.publish_status === 'published' ? 'published' : 'unpublished')
            : (realProfile?.ui_preferences?.default_new_property_publish === 'unpublished' ? 'unpublished' : 'published'));
    const [shareStatus, setShareStatus] = useState<'shared' | 'not_shared'>(
        initialData
            ? (initialData.share_status === 'Compartida' || initialData.share_status === 'shared' ? 'shared' : 'not_shared')
            : (realProfile?.ui_preferences?.default_new_property_share === 'shared' ? 'shared' : 'not_shared'));
    // Only relevant when editing a draft: lets the agent promote crm_status
    // from 'draft' to 'active' on save. Publicar / Compartir are gated on this
    // — a draft cannot be published or shared in one step without also being
    // activated, otherwise it stays in the "Borradores" tab forever (see the
    // "stuck in borrador" bug). New properties always land as 'active' via the
    // INSERT handler in PropertiesPage, so this toggle is hidden for them.
    const isEditingDraft = (initialData as any)?.crm_status === 'draft';
    const [activateProperty, setActivateProperty] = useState<boolean>(!isEditingDraft);

    // The ii_id-defining params (captación, operation, property type) are locked
    // once the property has a minted ii_id — the letters encode those attributes
    // and the ii_id is baked into the public /propiedades/{ii_id}-slug URL, so
    // changing them would desync the code and break its SEO URL. To change them,
    // duplicate the property: the copy is a draft with ii_id=NULL, editable until
    // activation, which mints a fresh, correct code. Drafts (incl. duplicated
    // ones) stay editable, so key the lock on ii_id presence, NOT on isEdit.
    const iiIdLocked = !!(initialData as any)?.ii_id;

    // Terreno dual unit: lot_size_varas (varas²) is the stored value; lotM2 is a
    // linked convenience input. Editing either box updates the other + formData.
    const [lotVaras, setLotVaras] = useState<string>(initialData?.lot_size_varas ? String(initialData.lot_size_varas) : '');
    const [lotM2, setLotM2] = useState<string>(initialData?.lot_size_varas ? String(round2(initialData.lot_size_varas * M2_PER_VARA2)) : '');

    // Image Upload State
    const [galleryPreviews, setGalleryPreviews] = useState<string[]>(initialData?.gallery?.length ? initialData.gallery : (initialData?.cover_photo_url || initialData?.image ? [initialData.cover_photo_url || initialData.image || ''] : []));
    // Optional per-photo captions keyed by URL — keying by URL (not index)
    // means reorder/remove/set-cover never need to touch this map.
    const [photoLabels, setPhotoLabels] = useState<Record<string, string>>(initialData?.photo_labels || {});
    const [dragActive, setDragActive] = useState(false);
    const [draggedImgIndex, setDraggedImgIndex] = useState<number | null>(null);
    const [isProcessingImages, setIsProcessingImages] = useState(false);
    // Live per-photo progress for the dropzone. Replaces the indeterminate
    // spinner so a big batch on mobile data doesn't look frozen. `done` counts
    // photos that finished (success OR failure) so the bar always reaches total.
    const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // OG thumbnail (800×420 JPEG) for fast WhatsApp/FB link previews. Captured
    // when the FIRST photo of a fresh property upload completes — that photo
    // becomes the cover by default. Cleared when the cover changes via
    // setAsCover or by removing the first photo, since we don't have a thumb
    // for arbitrary previously-uploaded photos. Edge function falls back to
    // weserv proxy when this is undefined.
    const [coverOgThumbUrl, setCoverOgThumbUrl] = useState<string | undefined>(undefined);

    // Document State — files staged in-memory until handleSave uploads them
    // via uploadPropertyDocuments (after the property row exists).
    const [documents, setDocuments] = useState<{ name: string, size: number, type: string, file: File }[]>([]);
    const [docError, setDocError] = useState<string | null>(null);
    const [docsUploading, setDocsUploading] = useState(false);
    const [dragActiveDocs, setDragActiveDocs] = useState(false);
    const docInputRef = useRef<HTMLInputElement>(null);
    const descriptionRef = useRef<HTMLTextAreaElement>(null);
    const gtqRate = useGtqRate();

    const [formData, setFormData] = useState<Partial<Property>>(initialData ? {
        ...initialData,
        location: { city: initialData.sector_name || '', state: initialData.department_name || '', sector: initialData.city_name || '' },
        type: initialData.property_type_name || '',
        amenities: initialData.amenities || initialData.services || [],
        // Legacy-imported notes are HTML; the editor speaks markdown.
        agent_notes: htmlNotesToMarkdown(initialData.agent_notes),
    } : {
        title: '',
        price: 0,
        currency: 'USD',
        price_label: '',
        location: { city: '', state: '', sector: '' },
        property_type_id: undefined,
        project_id: 1, // 1 = Directo, 2 = Compartida
        type: '',
        operation: 'sale',
        description: '',
        status: 'Disponible',
        publish_status: 'unpublished',
        share_status: 'not_shared',
        bedrooms: 0,
        bathrooms: 0,
        built_up_area: 0,
        parking: 0,
        amenities: [],
        gallery: [],
        video_url: '',
        owner_id: '',
    });

    // Resolve the selected owner's display name. Usually present in the
    // loaded owners list; falls back to a by-id fetch for owners outside the
    // caller's visibility (e.g. an agent editing a property whose owner
    // belongs to a teammate — fetchOwners narrows the list for agents).
    useEffect(() => {
        const id = formData.owner_id;
        if (!id || id === 'create_new') { setResolvedOwnerName(''); return; }
        const local = owners.find((o: any) => String(o.id) === String(id));
        if (local) { setResolvedOwnerName(local.name); return; }
        if (!supabase) return;
        let cancelled = false;
        supabase.from('contacts').select('name').eq('id', id).maybeSingle().then(({ data }) => {
            if (!cancelled) setResolvedOwnerName(data?.name || 'Propietario asignado');
        });
        return () => { cancelled = true; };
    }, [formData.owner_id, owners]);

    const filteredOwners = useMemo(() => {
        const q = ownerSearch.trim().toLowerCase();
        if (!q) return owners.slice(0, 8);
        return owners.filter((o: any) => (o.name || '').toLowerCase().includes(q)).slice(0, 8);
    }, [owners, ownerSearch]);

    // Default the responsible agent.
    //   - New property: assign to current user (guarantees a valid default).
    //   - Edit with empty agent_id (migrated rows whose legacy agent_id didn't map):
    //     fall back to initialData.created_by, NOT the current viewer. The previous
    //     "default to profile.id on edit" silently reassigned ownership to whoever
    //     was editing — see incident 2026-05-06.
    //   - Edit with both agent_id and created_by null: leave null. Agency admins
    //     are forced to pick via validation; regular agents save without changing it.
    useEffect(() => {
        if (!profile?.id) return;
        if (formData.agent_id) return;
        const fallbackId = initialData
            ? (initialData as any).created_by
            : profile.id;
        if (!fallbackId) return;
        const isCurrentUser = fallbackId === profile.id;
        setFormData((prev: Partial<Property>) => ({
            ...prev,
            agent_id: fallbackId,
            agent: isCurrentUser
                ? {
                    ...((prev as any).agent || {}),
                    name: profile.full_name || '',
                    avatar: profile.photo_url || (prev as any).agent?.avatar || '',
                    role: 'Agente Asociado',
                }
                : (prev as any).agent,
        } as any));
    }, [profile?.id, profile?.full_name, profile?.photo_url, formData.agent_id, initialData]);

    // Validation state — flips to true on the first save attempt so errors appear only after submit
    const [saveAttempted, setSaveAttempted] = useState(false);
    // Guards against double-submit. The form's onSave does an INSERT into
    // properties + photo links; without this, an impatient user clicking the
    // button N times produced N duplicate rows (incident 2026-05-05 23:20 UTC).
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Which footer button kicked off the submit — drives the per-button spinner.
    const [submitKind, setSubmitKind] = useState<'save' | 'draft' | null>(null);

    const validation = useMemo(() => {
        const errs: Record<string, string> = {};
        const missing: string[] = [];
        const add = (key: string, label: string) => { errs[key] = 'Requerido'; missing.push(label); };

        if (!formData.title?.trim()) add('title', 'Título de la Propiedad');
        if (!initialData && !formData.project_id) add('project_id', 'Tipo de Captación');
        if (!formData.description?.trim()) add('description', 'Descripción');
        if (!formData.operation) add('operation', 'Operación');
        if (!formData.property_type_id) add('property_type_id', 'Tipo de Inmueble');
        if (!formData.price || formData.price <= 0) add('price', 'Precio');
        if (!formData.status) add('status', 'Estado en Plataforma');
        if (!formData.location?.state) add('department', 'Departamento');
        if (!formData.location?.sector) add('sector', 'Sector');
        if (profile?.role === 'agency_admin' && !formData.agent_id) add('agent_id', 'Agente Responsable');
        if (!formData.gallery?.length) add('gallery', 'Al menos 1 fotografía');

        return { errs, missing, isValid: missing.length === 0 };
    }, [formData, initialData, profile?.role]);

    const errors = saveAttempted ? validation.errs : {};

    // Initial fetch for departments
    useEffect(() => {
        if (!profile) return;
        const fetchDepartments = async () => {
            if (!supabase) return;
            try {
                const { data, error } = await supabase
                    .from('departments')
                    .select('id, name, latitude, longitude, sort_order')
                    .eq('is_active', true)
                    .order('sort_order')
                    .order('name');
                if (error) throw error;
                if (data) {
                    // Deduplicate by name as legacy DB contains imported duplicates
                    const uniqueDepts = Array.from(new Map(data.map((d: any) => [d.name, d])).values());
                    setDepartments(uniqueDepts);
                }
            } catch (err) {
                console.error('Error fetching departments:', err);
            }
        };

        const fetchAgents = async () => {
            if (!supabase) return;
            try {
                let query = supabase
                    .from('profiles')
                    .select('id, full_name')
                    .eq('status', 'active');
                
                if (profile?.agency_id) {
                    query = query.eq('agency_id', profile.agency_id);
                }

                const { data, error } = await query.order('full_name');
                if (error) throw error;
                if (data) setAgents(data.map((p: any) => ({ id: p.id, name: p.full_name })));
            } catch (err) {
                console.error('Error fetching agents:', err);
            }
        };

        const fetchAmenities = async () => {
            if (!supabase) return;
            try {
                const { data, error } = await supabase
                    .from('amenities')
                    .select('id, name, slug')
                    .eq('is_active', true)
                    .order('name', { ascending: true });
                if (error) throw error;
                if (data) setAvailableAmenities(data);
            } catch (err) {
                console.error('Error fetching amenities:', err);
            }
        };

        const fetchPropertyTypes = async () => {
            if (!supabase) return;
            try {
                const { data, error } = await supabase
                    .from('property_types')
                    .select('id, name, slug')
                    .eq('is_active', true)
                    .order('name', { ascending: true });
                if (error) throw error;
                if (data) setPropertyTypes(data);
            } catch (err) {
                console.error('Error fetching property types:', err);
            }
        };

        const fetchPriceTags = async () => {
            if (!supabase) return;
            try {
                const { data, error } = await supabase
                    .from('property_tags')
                    .select('id, name, slug, color')
                    .eq('is_active', true)
                    .order('name');
                if (error) throw error;
                if (data) setPriceTags(data);
            } catch (err) {
                console.error('Error fetching price tags:', err);
            }
        };

        const fetchOwners = async () => {
            if (!supabase) return;
            try {
                let query = supabase
                    .from('contacts')
                    .select('id, name')
                    .eq('is_owner', true);

                if (profile?.agency_id) {
                    query = query.eq('agency_id', profile.agency_id);
                }

                if (profile?.role === 'agent') {
                    query = query.or(`agent_id.eq.${profile.id},created_by.eq.${profile.id}`);
                }

                const { data, error } = await query.order('name');
                if (error) throw error;
                if (data) setOwners(data || []);
            } catch (err) {
                console.error('Error fetching owners:', err);
            }
        };

        const fetchProyectos = async () => {
            if (!supabase || !profile?.agency_id) return;
            try {
                const { data, error } = await supabase
                    .from('proyectos')
                    .select('id, nombre')
                    .eq('agency_id', profile.agency_id)
                    .eq('is_deleted', false)
                    .order('nombre');
                if (error) throw error;
                if (data) setProyectos(data);
            } catch (err) {
                console.error('Error fetching proyectos:', err);
            }
        };

        fetchDepartments();
        fetchAgents();
        fetchAmenities();
        fetchPropertyTypes();
        fetchPriceTags();
        fetchOwners();
        fetchProyectos();
    }, [profile?.id, profile?.role, profile?.agency_id]);

    // Edit mode: preload the property's existing tag assignments.
    useEffect(() => {
        const propId = initialData?.id;
        if (!supabase || !propId) return;
        supabase
            .from('property_tag_assignments')
            .select('tag_id')
            .eq('property_id', propId)
            .then(({ data, error }) => {
                if (data && !error) setSelectedTagIds(data.map(r => r.tag_id));
            });
    }, [initialData?.id]);

    // Fetch sectors when department changes
    useEffect(() => {
        const fetchSectors = async () => {
            if (!supabase || !formData.location?.state) {
                setSectors([]);
                setCities([]);
                return;
            }
            try {
                // Find selected department
                const selectedDept = departments.find(d => d.name === formData.location?.state);
                if (!selectedDept) {
                    setSectors([]);
                    return;
                }

                const { data, error } = await supabase
                    .from('cities')
                    .select('id, name, department_id, latitude, longitude')
                    .eq('department_id', selectedDept.id)
                    .eq('is_active', true)
                    .order('name');
                
                if (error) throw error;
                setSectors(data || []);
            } catch (err) {
                console.error('Error fetching sectors:', err);
            }
        };
        fetchSectors();
    }, [formData.location?.state, departments]);

    // Fetch cities (sub-sectors) when sector changes
    useEffect(() => {
        const fetchCities = async () => {
            if (!supabase || !formData.location?.sector) {
                setCities([]);
                return;
            }
            try {
                // Find selected sector
                const selectedSector = sectors.find(s => s.name === formData.location?.sector);
                if (!selectedSector) {
                    setCities([]);
                    return;
                }

                const { data, error } = await supabase
                    .from('sectors')
                    .select('id, name, city_id, latitude, longitude')
                    .eq('city_id', selectedSector.id)
                    .eq('is_active', true)
                    .order('name');
                
                if (error) throw error;
                setCities(data || []);
            } catch (err) {
                console.error('Error fetching cities:', err);
            }
        };
        fetchCities();
    }, [formData.location?.sector, sectors]);

    const [isTranscribing, setIsTranscribing] = useState(false);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<BlobPart[]>([]);

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = async () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                await handleTranscription(audioBlob);

                // Stop all tracks to release the microphone
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorder.start();
            setIsRecording(true);
        } catch (error) {
            console.error('Error accessing microphone:', error);
            alert('No se pudo acceder al micrófono. Por favor, verifica los permisos.');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
        }
    };

    const toggleRecording = () => {
        if (isRecording) {
            stopRecording();
        } else {
            startRecording();
        }
    };

    const handleTranscription = async (audioBlob: Blob) => {
        if (!supabase) return;
        setIsTranscribing(true);

        try {
            // Create FormData to send the file
            const formDataToUpload = new FormData();
            formDataToUpload.append('file', audioBlob, 'recording.webm');

            const { data, error } = await supabase.functions.invoke('transcribe-audio', {
                body: formDataToUpload,
                // Supabase handles the auth headers automatically
            });

            if (error) {
                console.error("Error invoking edge function:", error);
                // Quota / daily-limit / too-long answers carry a Spanish message.
                const body = await (error as any).context?.json?.().catch(() => null);
                const known = String(body?.code ?? '').startsWith('AI_') || (error as any).context?.status === 413;
                alert(known && body?.error ? body.error : "Hubo un error al transcribir el audio.");
                throw error;
            }

            if (data?.text) {
                setInputText(prev => (prev.trim() + ' ' + data.text).trim());
            }
        } catch (error) {
            console.error('Transcription error:', error);
        } finally {
            setIsTranscribing(false);
        }
    };

    const handleAnalyze = async () => {
        if (!inputText.trim()) return;
        setIsAnalyzing(true);
        setAiFillSummary(null);
        try {
            const details = await extractPropertyDetails(inputText, {
                knownTypes: propertyTypes.map((t: any) => t.name),
                knownAmenities: availableAmenities.map((a: any) => a.name),
            });

            const filled: string[] = [];
            const updates: Record<string, any> = {};

            if (details.title) { updates.title = details.title; filled.push('título'); }
            // AI extracts USD amounts — reset to USD entry so a previously
            // toggled Q field can't keep a stale price_gtq alongside it.
            if (details.price && details.price > 0) { updates.price = Number(details.price); updates.currency = 'USD'; updates.price_gtq = null; filled.push('precio'); }
            // Skip the ii_id-defining fields (operation, tipo) when the code is
            // already minted — they're locked; changing them requires duplicating.
            if (!iiIdLocked && (details.operation === 'sale' || details.operation === 'rent')) { updates.operation = details.operation; filled.push('operación'); }
            if (details.description) { updates.description = details.description; filled.push('descripción'); }

            // Type name → property_type_id (normalized equality, then includes).
            if (!iiIdLocked && details.type) {
                const q = normalize(details.type);
                const typeMatch = propertyTypes.find((t: any) => normalize(t.name) === q)
                    ?? propertyTypes.find((t: any) => normalize(t.name).includes(q) || q.includes(normalize(t.name)));
                if (typeMatch) {
                    updates.property_type_id = typeMatch.id;
                    updates.type = typeMatch.name;
                    filled.push('tipo de inmueble');
                }
            }

            const specs = details.specs || {};
            if (specs.beds) { updates.bedrooms = Number(specs.beds); filled.push('habitaciones'); }
            if (specs.baths) { updates.bathrooms = Number(specs.baths); filled.push('baños'); }
            if (specs.area) { updates.built_up_area = Number(specs.area); filled.push('construcción'); }
            if (specs.parking) { updates.parking = Number(specs.parking); filled.push('parqueos'); }
            if (specs.yearBuilt) { updates.year_built = Number(specs.yearBuilt); filled.push('año de construcción'); }

            // price_label only when it resolves to a known tag; maintenance is Q.
            if (details.price_label) {
                const tag = findByName(priceTags, details.price_label);
                if (tag) { updates.price_label = tag.name; filled.push('etiqueta de precio'); }
            }
            if (typeof details.maintenance === 'number' && details.maintenance > 0) {
                updates.maintenance = details.maintenance;
                filled.push('mantenimiento');
            }

            // Amenities: resolve extracted names against the loaded catalog
            // (accent/case-insensitive); unmatched names are dropped.
            const extractedAmenities = details.amenities || [];
            const resolvedAmenityNames = availableAmenities
                .filter((a: any) => extractedAmenities.some((ea: string) => normalize(ea) === normalize(a.name)))
                .map((a: any) => a.name);
            if (resolvedAmenityNames.length > 0) {
                updates.amenities = resolvedAmenityNames;
                filled.push('amenidades');
            }

            // Location: resolve names → IDs level by level, best-effort (leave
            // unmatched levels empty). formData.location carries state =
            // Departamento (L2), sector = DB `cities` row (L3), city = DB
            // `sectors` row (L4). Never write the raw AI string into location.
            const loc: { department?: string; zone_or_city?: string; subsector?: string } =
                typeof details.location === 'string' ? { department: details.location } : (details.location ?? {});
            const deptMatch = findByName(departments, loc.department);
            let l3Match: any;
            let l4Match: any;
            if (deptMatch && loc.zone_or_city && supabase) {
                const { data: l3Rows } = await supabase
                    .from('cities')
                    .select('id, name')
                    .eq('department_id', deptMatch.id)
                    .eq('is_active', true);
                if (l3Rows?.length) {
                    const q = normalize(loc.zone_or_city);
                    const qZone = zoneNumber(q);
                    l3Match = l3Rows.find((r: any) => normalize(r.name) === q)
                        ?? (qZone != null ? l3Rows.find((r: any) => zoneNumber(normalize(r.name)) === qZone) : undefined)
                        ?? l3Rows.find((r: any) => normalize(r.name).startsWith(q));
                }
            }
            if (l3Match && loc.subsector && supabase) {
                const { data: l4Rows } = await supabase
                    .from('sectors')
                    .select('id, name')
                    .eq('city_id', l3Match.id)
                    .eq('is_active', true);
                if (l4Rows?.length) l4Match = findByName(l4Rows, loc.subsector);
            }
            if (deptMatch) filled.push('ubicación');

            // Names + IDs set together in ONE update: the cascade effects fetch
            // the dropdown option lists from the names, and the selects bind by
            // name string. Unmatched lower levels are cleared so no stale ID
            // survives under a different department.
            setFormData(prev => ({
                ...prev,
                ...updates,
                ...(deptMatch ? {
                    department_id: deptMatch.id,
                    city_id: l3Match?.id,
                    sector_id: l4Match?.id,
                    location: {
                        state: deptMatch.name,
                        sector: l3Match?.name || '',
                        city: l4Match?.name || '',
                    },
                } : {}),
            } as Partial<Property>));

            setAiFillSummary(filled.length > 0
                ? { ok: true, text: `Campos completados: ${filled.join(', ')}.` }
                : { ok: false, text: 'No se detectaron campos en la descripción. Intenta con más detalles.' });
        } catch (e) {
            alertAIError(e, () => navigate('/configuracion?tab=ia'));
        } finally {
            setIsAnalyzing(false);
        }
    };

    // «Mejorar con IA» — rewrites the description via the AI proxy, keeping the
    // previous text in a ref for a single-step «Deshacer».
    const handleEnhanceDescription = async () => {
        const current = formData.description || '';
        if (!current.trim() || isEnhancing) return;
        setIsEnhancing(true);
        try {
            const improved = await enhanceDescription(formData.title || '', current);
            if (improved && improved.trim() && improved !== current) {
                prevDescriptionRef.current = current;
                setCanUndoEnhance(true);
                setFormData(prev => ({ ...prev, description: improved }));
            }
        } catch (e) {
            alertAIError(e, () => navigate('/configuracion?tab=ia'));
        } finally {
            setIsEnhancing(false);
        }
    };

    const handleUndoEnhance = () => {
        if (prevDescriptionRef.current === null) return;
        const previous = prevDescriptionRef.current;
        prevDescriptionRef.current = null;
        setCanUndoEnhance(false);
        setFormData(prev => ({ ...prev, description: previous }));
    };

    // Unsaved-work guard. Baseline is snapshotted on the FIRST user interaction
    // (not at mount — mount effects like the agent default mutate formData and
    // would read as dirty). Parent navigates away on save, so no reset needed.
    const touchedRef = useRef(false);
    const baseFormRef = useRef(formData);
    const baseGalleryRef = useRef(galleryPreviews);
    const markTouched = () => {
        if (touchedRef.current) return;
        touchedRef.current = true;
        baseFormRef.current = formData;
        baseGalleryRef.current = galleryPreviews;
    };
    useUnsavedChangesGuard(
        touchedRef.current && !isSubmitting
        && (formData !== baseFormRef.current || galleryPreviews !== baseGalleryRef.current),
    );

    const handleSave = async (asDraft = false) => {
        if (isSubmitting) return;
        if (asDraft) {
            // Drafts only need the DB-minimum identity fields (title +
            // operation are the only NOT NULL columns without defaults) —
            // the point of a draft is saving half-finished capture work.
            const draftMissing: string[] = [];
            if (!formData.title?.trim()) draftMissing.push('Título de la Propiedad');
            if (!formData.operation) draftMissing.push('Operación');
            if (draftMissing.length > 0) {
                alert(`Para guardar como borrador completa:\n\n• ${draftMissing.join('\n• ')}`);
                return;
            }
        } else if (!validation.isValid) {
            setSaveAttempted(true);
            alert(`Por favor completa los campos requeridos:\n\n• ${validation.missing.join('\n• ')}`);
            const firstKey = Object.keys(validation.errs)[0];
            setTimeout(() => {
                const el = document.querySelector(`[data-field="${firstKey}"]`);
                el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 0);
            return;
        }
        setSaveAttempted(false);
        // When activating a draft, allow publish/share to flow through. When
        // staying as draft (or saving a NEW property as draft), force both off
        // so we never write a "published draft" row — that's the exact state
        // that caused the "stuck in borrador" bug.
        const stayingDraft = asDraft || (isEditingDraft && !activateProperty);
        const effectivePublish = stayingDraft ? 'unpublished' : publishStatus;
        const effectiveShare = stayingDraft ? 'not_shared' : shareStatus;
        const newProperty: Property = {
            ...(formData as Property),
            // ALL-CAPS titles get normalized to title case at save time — the
            // portals inherit clean data instead of shouting listings. Mixed-case
            // input passes through untouched (see lib/title-format.ts).
            title: smartTitleCase((formData.title || '').trim()),
            property_type_name: formData.type as string,
            department_name: (formData.location as any)?.state as string,
            city_name: (formData.location as any)?.sector as string,
            sector_name: (formData.location as any)?.city as string,
            publish_status: effectivePublish,
            share_status: effectiveShare,
            // Save-as-draft wins; otherwise promote draft → active when the
            // toggle is on. For normal saves of new properties and edits of
            // already-active rows, leave whatever was in formData so this
            // can't accidentally un-activate an active listing.
            ...(asDraft
                ? { crm_status: 'draft' as const }
                : (isEditingDraft ? { crm_status: activateProperty ? 'active' as const : 'draft' as const } : {})),
            id: initialData?.id || ('p' + Math.floor(Math.random() * 100000).toString()),
            // Transient field — onSave handler in PropertiesPage destructures
            // and uses it for the cover photo's og_thumb_url, NOT a column on
            // the properties table. Falls back to weserv when undefined.
            cover_og_thumb_url: coverOgThumbUrl,
            // Per-photo captions — parent writes them onto the property_photos
            // rows it inserts; not a column on properties.
            photo_labels: photoLabels,
        } as Property & { cover_og_thumb_url?: string };
        // Pass staged document files through to the parent's onSave handler
        // via a transient field; the parent uploads them after the property
        // INSERT/UPDATE succeeds and before it navigates away.
        (newProperty as any).pending_documents = documents.map(d => d.file);
        // Transient — parent writes property_tag_assignments after the row exists.
        (newProperty as any).tag_ids = selectedTagIds;
        setSubmitKind(asDraft ? 'draft' : 'save');
        setIsSubmitting(true);
        try {
            if (documents.length > 0) setDocsUploading(true);
            await onSave(newProperty);
        } finally {
            // Parent handler navigates away on success; the unmount makes this
            // a no-op then. On failure the button re-enables so the user can retry.
            setIsSubmitting(false);
            setSubmitKind(null);
            setDocsUploading(false);
        }
    };

    // Stable so the memoized LocationMap doesn't re-render (and re-init its
    // Google Map / AdvancedMarker) every time unrelated form state changes —
    // notably the gallery upload progress ticking during a big-photo batch.
    const handleCoordinatesChange = useCallback((lat: string, lng: string) => {
        setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));
    }, []);

    // Progressive map zoom per location level (deeper selection = closer in).
    const ZOOM = { department: 10, sector: 13, subsector: 15 } as const;
    // Bumped on each location selection so LocationMap pans + zooms to the pick.
    const focusTokenRef = useRef(0);
    const [mapFocus, setMapFocus] = useState<MapFocus | null>(null);
    const focusMap = useCallback((opts: Omit<MapFocus, 'token'>) => {
        focusTokenRef.current += 1;
        setMapFocus({ ...opts, token: focusTokenRef.current });
    }, []);

    const handleFiles = async (files: FileList | null) => {
        if (!files) return;

        const currentCount = galleryPreviews.length;
        const remaining = 17 - currentCount;

        if (remaining <= 0) {
            alert("Has alcanzado el límite máximo de 17 fotos.");
            return;
        }

        const selected = Array.from(files);
        const validFiles = selected.filter(isAcceptableImage).slice(0, remaining);

        if (validFiles.length === 0) {
            // Never fail silently. If the user picked files but none survived the
            // filter, say so — the old bare `return` is exactly what made the
            // Samsung empty-MIME drop look like the app doing nothing.
            if (selected.length > 0) {
                alert('No se pudo leer ese archivo como imagen. Usa una foto JPG, PNG, WEBP o HEIC.');
            }
            return;
        }

        if (!profile?.agency_id) {
            alert("No se pudo determinar la agencia. Vuelve a iniciar sesión.");
            return;
        }

        setIsProcessingImages(true);
        setUploadProgress({ done: 0, total: validFiles.length });
        const baseIdx = currentCount;
        const wasEmpty = currentCount === 0;

        // Process with bounded concurrency instead of Promise.all over every
        // file. Running all optimizations + uploads at once overran iOS Safari's
        // memory budget (cover photos failing to encode) and, because Promise.all
        // is fail-fast, a single bad photo or network blip discarded the entire
        // batch. A small pool keeps memory bounded; results[i] preserves order.
        // Adaptive concurrency: heavy photos (RAW/DNG previews, big JPEGs) each
        // hold a large decode buffer, so running three at once can exhaust iOS
        // Safari's memory budget. Process those one at a time; small photos
        // still get the parallel pool.
        const CONCURRENCY = validFiles.some(isHeavyImage) ? 1 : 3;
        type UploadResult = { url: string; ogThumbUrl?: string };
        const results: (UploadResult | null)[] = new Array(validFiles.length).fill(null);
        let firstError: unknown = null;

        const uploadOne = async (file: File, i: number): Promise<UploadResult> => {
            const blob = await optimizeImageToBlob(file);
            // Generate og-thumb only for the first photo of a fresh property
            // upload — that photo becomes the cover.
            const isCoverUpload = wasEmpty && i === 0;
            try {
                return await uploadGalleryBlob(blob, profile.agency_id!, baseIdx + i, isCoverUpload);
            } catch {
                // One retry — transient network failures are common on mobile data.
                // Pause first: an instant retry reuses the same wedged connection
                // and just fails again (that's why GlitchTip #118 shows batches
                // where the retry died too).
                await new Promise((r) => setTimeout(r, 800));
                return await uploadGalleryBlob(blob, profile.agency_id!, baseIdx + i, isCoverUpload);
            }
        };

        let cursor = 0;
        const runWorker = async () => {
            while (cursor < validFiles.length) {
                const i = cursor++;
                try {
                    results[i] = await uploadOne(validFiles[i], i);
                } catch (error) {
                    if (firstError === null) firstError = error;
                    console.error(`Error optimizing/uploading image ${i}:`, error);
                } finally {
                    setUploadProgress(p => (p ? { ...p, done: p.done + 1 } : p));
                }
            }
        };

        try {
            await Promise.all(
                Array.from({ length: Math.min(CONCURRENCY, validFiles.length) }, runWorker)
            );
        } finally {
            setIsProcessingImages(false);
            setUploadProgress(null);
        }

        const succeeded = results.filter((r): r is UploadResult => r !== null);
        const uploadedUrls = succeeded.map(r => r.url);
        const failedCount = validFiles.length - succeeded.length;

        if (uploadedUrls.length > 0) {
            setGalleryPreviews(prev => [...prev, ...uploadedUrls]);
            setFormData(prev => ({
                ...prev,
                gallery: [...(prev.gallery || []), ...uploadedUrls]
            }));
            if (wasEmpty && results[0]?.ogThumbUrl) {
                setCoverOgThumbUrl(results[0].ogThumbUrl);
            }
        }

        if (failedCount > 0) {
            // Report to GlitchTip — the old catch was silent, so we had zero
            // telemetry on a failure multiple users were hitting.
            reportError(firstError ?? new Error('Gallery image upload failed'), {
                feature: 'property_gallery_upload',
                failedCount,
                total: validFiles.length,
                succeeded: uploadedUrls.length,
                // Which files failed (name/type/size) — without this a 413/400
                // in the tracker is undiagnosable (GlitchTip #787). Capped at 5.
                failedFiles: validFiles
                    .filter((_, i) => results[i] === null)
                    .slice(0, 5)
                    .map(f => `${f.name} (${f.type || 'sin-mime'}, ${Math.round(f.size / 1024)} KB)`),
            });
            const detail = firstError ? `\n\nDetalle: ${coerceError(firstError).message}` : '';
            alert(
                (uploadedUrls.length > 0
                    ? `Se subieron ${uploadedUrls.length} de ${validFiles.length} fotos. ${failedCount} no se pudieron subir; vuelve a intentarlo con esas.`
                    : "Hubo un error al subir las imágenes. Por favor, intenta de nuevo.") + detail
            );
        }
    };

    const onDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(true);
    };

    const onDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    };

    const onDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFiles(e.dataTransfer.files);
        }
    };

    const removeImage = (index: number) => {
        setGalleryPreviews(prev => {
            const updatedPreviews = prev.filter((_, i) => i !== index);
            setFormData(f => ({
                ...f,
                gallery: (f.gallery || []).filter((_, i) => i !== index),
                cover_photo_url: updatedPreviews.length > 0 ? updatedPreviews[0] : undefined
            }));
            return updatedPreviews;
        });
        // If we removed index 0, the new cover is a different photo — we
        // don't have a thumb for it. Fall back to weserv on the edge function.
        if (index === 0) setCoverOgThumbUrl(undefined);
    };

    const setAsCover = (url: string) => {
        setGalleryPreviews(prev => {
            const index = prev.indexOf(url);
            if (index > -1) {
                const newPreviews = [...prev];
                newPreviews.splice(index, 1);
                newPreviews.unshift(url);

                setFormData(f => ({
                    ...f,
                    gallery: newPreviews,
                    cover_photo_url: newPreviews[0]
                }));

                return newPreviews;
            }
            return prev;
        });
        // The new cover is a previously-uploaded photo without a tracked
        // thumb. Edge function falls back to weserv.
        setCoverOgThumbUrl(undefined);
    };

    const handleDragStartImg = (e: React.DragEvent, index: number) => {
        setDraggedImgIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOverImg = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    /** Shared gallery reorder — used by BOTH desktop drag-and-drop and the touch
     *  move buttons. HTML5 drag events never fire on touch, so phones had no way
     *  to reorder photos at all; the buttons are the only path there. Keeps
     *  formData.gallery + cover_photo_url in sync with the previews. */
    const moveImage = (from: number, to: number) => {
        setGalleryPreviews(prev => {
            if (from === to || from < 0 || to < 0 || from >= prev.length || to >= prev.length) return prev;
            const newPreviews = [...prev];
            const [item] = newPreviews.splice(from, 1);
            newPreviews.splice(to, 0, item);

            setFormData(f => ({
                ...f,
                gallery: newPreviews,
                cover_photo_url: newPreviews[0]
            }));

            return newPreviews;
        });
        // The cover slot changed hands, so the tracked thumb belongs to the OLD
        // cover — drop it and let the edge fn fall back to weserv. Same rule as
        // removeImage/setAsCover; the drag path silently skipped this before.
        if (from === 0 || to === 0) setCoverOgThumbUrl(undefined);
    };

    const handleDropImg = (e: React.DragEvent, targetIndex: number) => {
        e.preventDefault();
        if (draggedImgIndex === null || draggedImgIndex === targetIndex) return;
        moveImage(draggedImgIndex, targetIndex);
        setDraggedImgIndex(null);
    };

    const handleDocFiles = (files: FileList | null) => {
        if (!files || files.length === 0) return;
        const accepted: { name: string, size: number, type: string, file: File }[] = [];
        const rejected: string[] = [];
        for (const file of Array.from(files)) {
            const v = validatePropertyDoc(file);
            if (v) {
                rejected.push(v.message);
                continue;
            }
            accepted.push({ name: file.name, size: file.size, type: file.type, file });
        }
        if (accepted.length > 0) setDocuments(prev => [...prev, ...accepted]);
        setDocError(rejected.length > 0 ? rejected.join('\n') : null);
    };

    const removeDocument = (index: number) => {
        setDocuments(prev => prev.filter((_, i) => i !== index));
    };

    return (
        <div onPointerDownCapture={markTouched} onKeyDownCapture={markTouched} className={clsx("flex flex-col gap-8 max-w-5xl mx-auto w-full pb-10", theme !== 'dark' ? 'text-slate-900' : 'text-slate-100')}>

            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        onClick={onCancel}
                        className={clsx(
                            "flex items-center gap-2 p-2 px-4 rounded-xl transition-all font-bold text-sm",
                            theme !== 'dark'
                                ? "text-slate-600 hover:bg-slate-100 hover:text-indigo-600"
                                : "text-slate-400 hover:bg-white/10 hover:text-white"
                        )}
                    >
                        <ArrowLeft size={18} />
                        Volver
                    </button>
                </div>
            </div>

            {/* AI Panel — Asistente de Ingreso Rápido (subscription-gated) */}
            {!aiQuota.loading && (aiQuota.hasSubscription ? (
                <div className={clsx("stagger-item p-6 rounded-2xl border relative overflow-hidden", theme !== 'dark' ? 'bg-indigo-50/50 border-indigo-100' : 'bg-indigo-500/5 border-indigo-500/20')} style={{ '--i': 1 } as React.CSSProperties}>
                    <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 blur-[80px] rounded-full" />
                    <div className="relative z-10 flex flex-col gap-4">
                        <div className="flex items-start gap-4">
                            <div className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center bg-indigo-500/15 text-indigo-500">
                                <Sparkles size={22} />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-bold text-indigo-500">Asistente de Ingreso Rápido</h3>
                                <p className="text-sm opacity-80 max-w-2xl">
                                    Describe o dicta la propiedad y la IA completará los campos automáticamente.
                                </p>
                            </div>
                        </div>
                        <textarea
                            rows={4}
                            placeholder="Ej: Apartamento en zona 14, 2 habitaciones, 2 baños, 120 m², $185,000, con gimnasio y piscina…"
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            className={clsx(
                                "w-full px-4 py-3 rounded-xl border focus:outline-none focus:border-indigo-500 transition-colors font-medium text-sm",
                                theme !== 'dark' ? "bg-white border-slate-200 text-slate-900 placeholder:text-slate-400" : "bg-black/20 border-white/10 text-white placeholder:text-slate-500"
                            )}
                        />
                        <div className="flex flex-wrap items-center gap-3">
                            <button
                                type="button"
                                onClick={toggleRecording}
                                disabled={isTranscribing}
                                title={isRecording ? 'Detener dictado' : 'Dictar con el micrófono'}
                                className={clsx(
                                    "shrink-0 w-11 h-11 rounded-xl flex items-center justify-center border transition-all disabled:opacity-60 disabled:cursor-not-allowed",
                                    isRecording
                                        ? "bg-rose-500 border-rose-500 text-white animate-pulse shadow-lg shadow-rose-500/30"
                                        : theme !== 'dark'
                                            ? "bg-white border-slate-200 text-indigo-600 hover:border-indigo-400"
                                            : "bg-white/5 border-white/10 text-indigo-400 hover:border-indigo-500/50"
                                )}
                            >
                                {isTranscribing ? <Loader2 size={18} className="animate-spin" /> : <Mic size={18} />}
                            </button>
                            <button
                                type="button"
                                onClick={handleAnalyze}
                                disabled={isAnalyzing || !inputText.trim()}
                                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-3 rounded-xl font-bold shadow-lg shadow-indigo-600/20 transition-all hover:scale-105 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                            >
                                {isAnalyzing ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
                                {isAnalyzing ? 'Analizando…' : 'Completar campos con IA'}
                            </button>
                        </div>
                        {aiFillSummary && (
                            <p className={clsx("text-xs font-medium", aiFillSummary.ok ? "text-emerald-500" : "text-amber-500")}>
                                {aiFillSummary.text}
                            </p>
                        )}
                    </div>
                </div>
            ) : (
                <div className={clsx("stagger-item p-6 rounded-2xl border relative overflow-hidden", theme !== 'dark' ? 'bg-indigo-50/50 border-indigo-100' : 'bg-indigo-500/5 border-indigo-500/20')} style={{ '--i': 1 } as React.CSSProperties}>
                    <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 blur-[80px] rounded-full" />
                    <div className="relative z-10 flex flex-col sm:flex-row sm:items-center gap-4">
                        <div className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center bg-indigo-500/15 text-indigo-500">
                            <Sparkles size={22} />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-lg font-bold text-indigo-500">Asistente de Ingreso Rápido</h3>
                            <p className="text-sm opacity-80 max-w-2xl">
                                Describe o dicta la propiedad y la IA completará los campos automáticamente.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => navigate('/configuracion?tab=ia')}
                            className="shrink-0 flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-5 py-2.5 rounded-xl font-bold text-sm shadow-lg shadow-indigo-600/20 transition-all hover:scale-105 active:scale-95"
                        >
                            <Sparkles size={16} />
                            Activar Asistente IA
                        </button>
                    </div>
                </div>
            ))}

            {/* Form Details: Card-based Sections */}
            <div className="flex flex-col gap-6" style={{ '--i': 2 } as React.CSSProperties}>

                {/* ─ 1. Galería de Imágenes ─ */}
                <div data-field="gallery" data-tour="fotos" className={clsx("stagger-item p-6 sm:p-8 rounded-2xl border shadow-sm", theme !== 'dark' ? 'bg-white border-slate-200' : 'bg-slate-900 border-white/10', errors.gallery && "!border-rose-500")}>
                    <div className="flex items-center gap-3 mb-8">
                        <div className={clsx("p-2.5 rounded-xl", theme !== 'dark' ? "bg-indigo-50 text-indigo-600" : "bg-indigo-500/10 text-indigo-400")}>
                            <Camera size={20} />
                        </div>
                        <div>
                            <h3 className={clsx("text-lg font-black", theme !== 'dark' ? 'text-slate-800' : 'text-white')}>Galería de Imágenes *</h3>
                            <p className="text-xs text-slate-500 font-medium mt-0.5">Sube hasta 17 fotografías para destacar la propiedad</p>
                            {errors.gallery && <p className="text-xs text-rose-500 mt-1 font-medium">Sube al menos 1 fotografía</p>}
                        </div>
                    </div>

                    <div className="space-y-6">
                        {/* Drop Zone */}
                        <div
                            onDragOver={onDragOver}
                            onDragLeave={onDragLeave}
                            onDrop={onDrop}
                            onClick={() => fileInputRef.current?.click()}
                            className={clsx(
                                "relative group cursor-pointer border-2 border-dashed rounded-3xl p-12 transition-all duration-500 flex flex-col items-center justify-center text-center overflow-hidden",
                                dragActive
                                    ? "border-indigo-500 bg-indigo-500/10 scale-[0.99] rotate-1"
                                    : theme !== 'dark'
                                        ? "border-slate-200 bg-slate-50/50 hover:border-indigo-400 hover:bg-white"
                                        : "border-white/10 bg-black/20 hover:border-white/20 hover:bg-white/5"
                            )}
                        >
                            {/* Animated circle in background */}
                            <div className={clsx(
                                "absolute w-40 h-40 rounded-full blur-[60px] transition-all duration-700 opacity-0 group-hover:opacity-30",
                                theme !== 'dark' ? "bg-indigo-400" : "bg-indigo-600"
                            )} />

                            <input
                                type="file"
                                ref={fileInputRef}
                                className="hidden"
                                multiple
                                accept="image/*,.heic,.heif,.dng,.tif,.tiff"
                                onChange={(e) => handleFiles(e.target.files)}
                            />

                            {isProcessingImages ? (
                                <div className="flex flex-col items-center justify-center relative z-10 transition-all duration-500 text-indigo-500 w-full max-w-[300px]">
                                    <Loader2 size={48} className="animate-spin mb-4" />
                                    <h4 className={clsx("text-lg font-black mb-1", theme !== 'dark' ? 'text-slate-800' : 'text-white')}>
                                        {uploadProgress
                                            ? `Subiendo ${Math.min(uploadProgress.done + 1, uploadProgress.total)} de ${uploadProgress.total}…`
                                            : 'Optimizando imágenes...'}
                                    </h4>
                                    <p className="text-sm font-medium opacity-70 mb-3">Convirtiendo a formato WebP y ajustando resolución</p>
                                    {uploadProgress && (
                                        <div className={clsx(
                                            "w-full h-2 rounded-full overflow-hidden",
                                            theme !== 'dark' ? "bg-indigo-100" : "bg-white/10"
                                        )}>
                                            <div
                                                className="h-full bg-indigo-500 rounded-full transition-all duration-300 ease-out"
                                                style={{ width: `${Math.round((uploadProgress.done / uploadProgress.total) * 100)}%` }}
                                            />
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <div className={clsx(
                                        "mb-5 p-5 rounded-2xl transition-all duration-500",
                                        theme !== 'dark' ? "bg-white shadow-xl shadow-indigo-100 text-indigo-600" : "bg-white/10 text-indigo-400"
                                    )}>
                                        <UploadCloud size={32} className={clsx("transition-transform duration-500", dragActive ? "scale-110" : "group-hover:scale-110 group-hover:-translate-y-1")} />
                                    </div>

                                    <div className="relative z-10">
                                        <h4 className={clsx("text-lg font-black mb-2", theme !== 'dark' ? 'text-slate-800' : 'text-white')}>
                                            {dragActive ? "¡Suelta aquí!" : "Sueltas tus imágenes aquí"}
                                        </h4>
                                        <p className="text-sm text-slate-500 font-medium max-w-[280px]">
                                            o haz clic para explorar tus archivos (PNG, JPG o WEBP hasta 10MB)
                                        </p>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Image Previews Grid */}
                        <div className="flex flex-col gap-4">
                            <div className="flex justify-between items-center">
                                <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Cargadas ({galleryPreviews.length}/17)</span>
                                {galleryPreviews.length > 0 && (
                                    <span className="text-[10px] font-black text-indigo-500 uppercase">Selecciona la portada principal</span>
                                )}
                            </div>

                            {galleryPreviews.length > 0 && (
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                                    {/* Shared autocomplete for the per-photo caption inputs */}
                                    <datalist id="photo-label-presets">
                                        {PHOTO_LABEL_PRESETS.map(p => <option key={p} value={p} />)}
                                    </datalist>
                                    {galleryPreviews.map((url, idx) => {
                                        const isCover = formData.cover_photo_url === url;
                                        return (
                                            <div key={idx} className="flex flex-col gap-1.5">
                                            <div
                                                draggable
                                                onDragStart={(e) => handleDragStartImg(e, idx)}
                                                onDragOver={(e) => handleDragOverImg(e, idx)}
                                                onDrop={(e) => handleDropImg(e, idx)}
                                                className={clsx(
                                                    "group relative aspect-square rounded-2xl overflow-hidden border-2 transition-all duration-300 cursor-move",
                                                    isCover ? "border-indigo-500 shadow-lg shadow-indigo-500/20 scale-95" : "border-transparent",
                                                    draggedImgIndex === idx && "opacity-50 scale-95"
                                                )}
                                            >
                                                <img src={url} alt={`Preview ${idx}`} className="w-full h-full object-cover" />

                                                {/* Overlays.
                                                    `group-hover` alone hid the Eliminar / Poner de Portada buttons on
                                                    touch devices — phones have no hover, so the overlay stayed at
                                                    opacity-0 and users on mobile web literally could not delete a photo
                                                    ("le doy y no pasa nada"). Reveal it whenever the device can't hover. */}
                                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity flex flex-col justify-between p-2">
                                                    <div className="flex justify-between items-start gap-1">
                                                        {/* Move buttons — the ONLY way to reorder on touch, since the
                                                            tile's HTML5 drag-and-drop never fires there. Desktop keeps
                                                            drag too; these just work everywhere (and via keyboard).
                                                            Mirrors the native app's movePhoto chevrons. */}
                                                        <div className="flex gap-1">
                                                            {idx > 0 && (
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); moveImage(idx, idx - 1); }}
                                                                    className="p-1.5 rounded-lg bg-white/20 text-white hover:bg-white/40 backdrop-blur-md transition-colors shadow-lg"
                                                                    title="Mover a la izquierda"
                                                                    aria-label="Mover a la izquierda"
                                                                >
                                                                    <ChevronLeft size={14} />
                                                                </button>
                                                            )}
                                                            {idx < galleryPreviews.length - 1 && (
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); moveImage(idx, idx + 1); }}
                                                                    className="p-1.5 rounded-lg bg-white/20 text-white hover:bg-white/40 backdrop-blur-md transition-colors shadow-lg"
                                                                    title="Mover a la derecha"
                                                                    aria-label="Mover a la derecha"
                                                                >
                                                                    <ChevronRight size={14} />
                                                                </button>
                                                            )}
                                                        </div>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); removeImage(idx); }}
                                                            className="p-1.5 rounded-lg bg-rose-500 text-white hover:bg-rose-600 transition-colors shadow-lg"
                                                            title="Eliminar imagen"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>

                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setAsCover(url); }}
                                                        className={clsx(
                                                            "w-full py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all",
                                                            isCover
                                                                ? "bg-indigo-500 text-white"
                                                                : "bg-white/20 text-white hover:bg-white/40 backdrop-blur-md"
                                                        )}
                                                    >
                                                        {isCover ? "Portada Principal" : "Poner de Portada"}
                                                    </button>
                                                </div>

                                                {/* Cover indicator tag */}
                                                {isCover && (
                                                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-indigo-500 text-[9px] font-black text-white shadow-lg">
                                                        PORTADA
                                                    </div>
                                                )}
                                            </div>

                                            {/* Optional caption — datalist gives preset suggestions, free text allowed */}
                                            <input
                                                type="text"
                                                list="photo-label-presets"
                                                maxLength={40}
                                                placeholder="Etiqueta (ej. Cocina)"
                                                value={photoLabels[url] || ''}
                                                onChange={(e) => setPhotoLabels(prev => ({ ...prev, [url]: e.target.value }))}
                                                className={clsx(
                                                    "w-full px-2.5 py-1.5 rounded-lg border text-xs font-medium focus:outline-none focus:border-indigo-500 transition-colors",
                                                    theme !== 'dark'
                                                        ? "bg-slate-50 border-slate-200 text-slate-700 placeholder:text-slate-400"
                                                        : "bg-black/20 border-white/10 text-white placeholder:text-slate-500"
                                                )}
                                            />
                                            </div>
                                        );
                                    })}

                                    {/* Add more small button */}
                                    {galleryPreviews.length < 17 && (
                                        <button
                                            onClick={() => fileInputRef.current?.click()}
                                            className={clsx(
                                                "aspect-square rounded-2xl border-2 border-dashed flex flex-col items-center justify-center gap-2 transition-all",
                                                theme !== 'dark'
                                                    ? "border-slate-200 text-slate-400 hover:border-indigo-400 hover:text-indigo-500 bg-slate-50/30"
                                                    : "border-white/10 text-slate-500 hover:border-white/30 hover:text-white bg-white/5"
                                            )}
                                        >
                                            <Plus size={24} />
                                            <span className="text-[10px] font-bold uppercase tracking-widest">Añadir más</span>
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Video URL */}
                        <div className="pt-6 border-t border-slate-100/10">
                            <label className="block text-xs font-bold mb-2 uppercase tracking-widest text-slate-500">Link de {VIDEO_LINK_LABEL}</label>
                            <div className="relative">
                                <div className={clsx(
                                    "absolute left-4 top-1/2 -translate-y-1/2 p-1.5 rounded-lg",
                                    theme !== 'dark' ? "bg-rose-50 text-rose-500" : "bg-rose-500/10 text-rose-500"
                                )}>
                                    <Video size={16} />
                                </div>
                                <input
                                    type="text"
                                    placeholder={VIDEO_LINK_PLACEHOLDER}
                                    value={formData.video_url || ''}
                                    onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                                    className={clsx(
                                        "w-full pl-14 pr-4 py-3 rounded-xl border focus:outline-none focus:border-indigo-500 transition-colors font-medium text-sm",
                                        theme !== 'dark' ? "bg-slate-50 border-slate-200 text-slate-900" : "bg-black/20 border-white/10 text-white"
                                    )}
                                />
                            </div>
                            {formData.video_url?.trim() && !videoLinkProvider(formData.video_url) ? (
                                <p className="mt-2 text-xs font-medium text-amber-600">{VIDEO_LINK_INVALID}</p>
                            ) : (
                                <p className="mt-2 text-xs text-slate-500">{VIDEO_LINK_HELP}</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* ─ 2. Información Básica ─ */}
                <div className={clsx("stagger-item p-6 sm:p-8 rounded-2xl border shadow-sm", theme !== 'dark' ? 'bg-white border-slate-200' : 'bg-slate-900 border-white/10')}>
                    <div className="flex items-center gap-3 mb-8">
                        <div className={clsx("p-2.5 rounded-xl", theme !== 'dark' ? "bg-indigo-50 text-indigo-600" : "bg-indigo-500/10 text-indigo-400")}>
                            <FileText size={20} />
                        </div>
                        <div>
                            <h3 className={clsx("text-lg font-black", theme !== 'dark' ? 'text-slate-800' : 'text-white')}>Información Básica</h3>
                            <p className="text-xs text-slate-500 font-medium mt-0.5">Identificación principal y descripción del inmueble</p>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                            <div className={clsx(!initialData ? "md:col-span-8" : "md:col-span-12")} data-field="title">
                                <label className="block text-xs font-bold mb-2 uppercase tracking-widest text-slate-500">Título de la Propiedad <span className="text-rose-500">*</span></label>
                                <input
                                    type="text"
                                    placeholder="Ej: Espectacular Penthouse en Zona 14"
                                    value={formData.title}
                                    maxLength={70}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    className={clsx("w-full px-4 py-3 rounded-xl border focus:outline-none focus:border-indigo-500 transition-colors font-medium", theme !== 'dark' ? "bg-slate-50 border-slate-200 text-slate-900" : "bg-black/20 border-white/10 text-white", errors.title && "!border-rose-500")}
                                />
                                {/* SEO guardrail: Google truncates titles past ~60 chars; 70 is the hard cap. */}
                                {(formData.title || '').length >= 55 && (
                                    <p className={clsx("text-xs mt-1 font-medium", (formData.title || '').length >= 70 ? "text-rose-500" : "text-amber-500")}>
                                        {(formData.title || '').length}/70 caracteres{(formData.title || '').length >= 70 ? ' — límite alcanzado' : ''}
                                    </p>
                                )}
                                {errors.title && <p className="text-xs text-rose-500 mt-1 font-medium">{errors.title}</p>}
                            </div>

                            {/* NOTE: `project_id` is "Tipo de Captación" (1 = Directo, 2 = Compartida),
                                NOT the development/project link. The project link is `proyecto_id` below.
                                Misleading legacy name — do not confuse the two. */}
                            <div className="md:col-span-4" data-field="project_id">
                                <label className="block text-xs font-bold mb-2 uppercase tracking-widest text-slate-500">
                                    Tipo de Captación <span className="text-rose-500">*</span>
                                </label>
                                <select
                                    value={formData.project_id || 1}
                                    onChange={(e) => setFormData({ ...formData, project_id: parseInt(e.target.value) })}
                                    disabled={iiIdLocked}
                                    className={clsx(
                                        "w-full px-4 py-3 rounded-xl border focus:outline-none focus:border-indigo-500 transition-colors font-medium text-sm",
                                        iiIdLocked ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
                                        theme !== 'dark' ? "bg-slate-50 border-slate-200 text-slate-900" : "bg-black/20 border-white/10 text-white",
                                        errors.project_id && "!border-rose-500"
                                    )}
                                >
                                    <option value={1} className={theme !== 'dark' ? "bg-white text-slate-900" : "bg-slate-800 text-white"}>Directo</option>
                                    <option value={2} className={theme !== 'dark' ? "bg-white text-slate-900" : "bg-slate-800 text-white"}>Compartida</option>
                                </select>
                                {iiIdLocked
                                    ? <p className="text-xs text-slate-400 mt-1 font-medium">No editable. Duplica la propiedad para cambiar la captación.</p>
                                    : errors.project_id && <p className="text-xs text-rose-500 mt-1 font-medium">{errors.project_id}</p>}
                            </div>
                        </div>

                        <div data-field="proyecto_id">
                            <label className="block text-xs font-bold mb-2 uppercase tracking-widest text-slate-500">
                                Proyecto <span className="text-slate-400 normal-case tracking-normal font-medium">(opcional)</span>
                            </label>
                            <select
                                value={formData.proyecto_id || ''}
                                onChange={(e) => setFormData({ ...formData, proyecto_id: e.target.value || null })}
                                className={clsx(
                                    "w-full px-4 py-3 rounded-xl border focus:outline-none focus:border-indigo-500 transition-colors font-medium text-sm cursor-pointer",
                                    theme !== 'dark' ? "bg-slate-50 border-slate-200 text-slate-900" : "bg-black/20 border-white/10 text-white"
                                )}
                            >
                                <option value="" className={theme !== 'dark' ? "bg-white text-slate-900" : "bg-slate-800 text-white"}>Sin proyecto</option>
                                {proyectos.map(p => (
                                    <option key={p.id} value={p.id} className={theme !== 'dark' ? "bg-white text-slate-900" : "bg-slate-800 text-white"}>{p.nombre}</option>
                                ))}
                            </select>
                            {proyectos.length === 0 && (
                                <p className="text-xs text-slate-400 mt-1 font-medium">
                                    Aún no tienes proyectos. Créalos desde la sección Proyectos.
                                </p>
                            )}
                        </div>

                        <div data-field="description">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-y-3 mb-3">
                                <div className="flex items-center gap-2">
                                    <label className="block text-xs font-bold uppercase tracking-widest text-slate-500">Descripción <span className="text-rose-500">*</span></label>
                                    {aiQuota.hasSubscription && !!formData.description?.trim() && (
                                        <button
                                            type="button"
                                            onClick={handleEnhanceDescription}
                                            disabled={isEnhancing}
                                            title="Reescribir la descripción con IA"
                                            className={clsx(
                                                "flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider border transition-colors disabled:opacity-60 disabled:cursor-not-allowed",
                                                theme !== 'dark'
                                                    ? "border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                                                    : "border-indigo-500/30 text-indigo-400 hover:bg-indigo-500/10"
                                            )}
                                        >
                                            {isEnhancing ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
                                            Mejorar con IA
                                        </button>
                                    )}
                                    {canUndoEnhance && !isEnhancing && (
                                        <button
                                            type="button"
                                            onClick={handleUndoEnhance}
                                            title="Restaurar la descripción anterior"
                                            className={clsx(
                                                "px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-colors",
                                                theme !== 'dark' ? "text-slate-500 hover:bg-slate-100" : "text-slate-400 hover:bg-white/10"
                                            )}
                                        >
                                            Deshacer
                                        </button>
                                    )}
                                </div>
                                <div className="flex flex-wrap justify-start sm:justify-end gap-1 flex-1">
                                    {['🏠', '🏢', '🏗️', '🏘️', '🏙️', '📍', '🗺️', '✨', '💎', '🔑', '🏷️', '💰', '🌳', '🌄', '🌇', '🏊‍♂️', '🚗', '🛋️', '🛏️', '🚿', '🍳', '📶', '🔐'].map(emoji => (
                                        <button
                                            key={emoji}
                                            type="button"
                                            onClick={() => {
                                                const textarea = descriptionRef.current;
                                                if (!textarea) return;

                                                const start = textarea.selectionStart;
                                                const end = textarea.selectionEnd;
                                                const text = formData.description || '';
                                                const before = text.substring(0, start);
                                                const after = text.substring(end);
                                                const newText = before + emoji + after;

                                                setFormData({ ...formData, description: newText });

                                                // Restore focus and cursor position after React re-renders
                                                setTimeout(() => {
                                                    textarea.focus();
                                                    textarea.setSelectionRange(start + emoji.length, start + emoji.length);
                                                }, 0);
                                            }}
                                            className={clsx(
                                                "w-7 h-7 flex items-center justify-center rounded-lg text-sm transition-all hover:scale-125 active:scale-90",
                                                theme !== 'dark' ? "bg-slate-100 hover:bg-slate-200" : "bg-white/5 hover:bg-white/10"
                                            )}
                                        >
                                            {emoji}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <textarea
                                ref={descriptionRef}
                                rows={4}
                                placeholder="Describe los detalles principales, vistas, acabados..."
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                className={clsx("w-full px-4 py-3 rounded-xl border focus:outline-none focus:border-indigo-500 transition-colors font-medium", theme !== 'dark' ? "bg-slate-50 border-slate-200 text-slate-900" : "bg-black/20 border-white/10 text-white", errors.description && "!border-rose-500")}
                            />
                            {errors.description && <p className="text-xs text-rose-500 mt-1 font-medium">{errors.description}</p>}
                        </div>

                        <div data-field="agent_notes">
                            <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
                                Notas Privadas
                                <span className={clsx("ml-2 inline-block px-2 py-0.5 rounded-chip text-[10px] font-bold normal-case tracking-normal", theme !== 'dark' ? "bg-amber-50 text-amber-700" : "bg-amber-500/10 text-amber-400")}>
                                    Solo visible para ti y tu agencia
                                </span>
                            </label>
                            <MarkdownEditor
                                value={formData.agent_notes ?? ''}
                                onChange={(next: string) => setFormData({ ...formData, agent_notes: next })}
                                theme={theme}
                                minHeight={140}
                                placeholder="Recordatorios, contactos del propietario, links de Drive, observaciones internas..."
                            />
                        </div>
                    </div>
                </div>

                {/* ─ 3. Clasificación y Precio ─ */}
                <div className={clsx("stagger-item p-6 sm:p-8 rounded-2xl border shadow-sm", theme !== 'dark' ? 'bg-white border-slate-200' : 'bg-slate-900 border-white/10')}>
                    <div className="flex items-center gap-3 mb-8">
                        <div className={clsx("p-2.5 rounded-xl", theme !== 'dark' ? "bg-indigo-50 text-indigo-600" : "bg-indigo-500/10 text-indigo-400")}>
                            <Tag size={20} />
                        </div>
                        <div>
                            <h3 className={clsx("text-lg font-black", theme !== 'dark' ? 'text-slate-800' : 'text-white')}>Clasificación y Precio</h3>
                            <p className="text-xs text-slate-500 font-medium mt-0.5">Tipo de transacción y valores asociados</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div data-field="operation">
                            <label className="block text-xs font-bold mb-2 uppercase tracking-widest text-slate-500">Operación <span className="text-rose-500">*</span></label>
                            <select
                                value={formData.operation}
                                onChange={(e) => setFormData({ ...formData, operation: e.target.value as any })}
                                disabled={iiIdLocked}
                                className={clsx("w-full px-4 py-3 rounded-xl border focus:outline-none focus:border-indigo-500 transition-colors font-medium appearance-none", iiIdLocked ? "opacity-60 cursor-not-allowed" : "cursor-pointer", theme !== 'dark' ? "bg-slate-50 border-slate-200 focus:bg-white text-slate-900" : "bg-black/20 border-white/10 text-white", errors.operation && "!border-rose-500")}
                            >
                                <option value="sale">Venta</option>
                                <option value="rent">Alquiler</option>
                            </select>
                            {iiIdLocked
                                ? <p className="text-xs text-slate-400 mt-1 font-medium">No editable. Duplica la propiedad para cambiar la operación.</p>
                                : errors.operation && <p className="text-xs text-rose-500 mt-1 font-medium">{errors.operation}</p>}
                        </div>
                        <div data-field="property_type_id">
                            <label className="block text-xs font-bold mb-2 uppercase tracking-widest text-slate-500">Tipo de Inmueble <span className="text-rose-500">*</span></label>
                            <select
                                value={formData.property_type_id || ''}
                                onChange={(e) => {
                                    const selectedId = Number(e.target.value);
                                    const selectedType = propertyTypes.find(t => t.id === selectedId);
                                    setFormData({
                                        ...formData,
                                        property_type_id: selectedId,
                                        type: selectedType?.name || ''
                                    });
                                }}
                                disabled={iiIdLocked}
                                className={clsx("w-full px-4 py-3 rounded-xl border focus:outline-none focus:border-indigo-500 transition-colors font-medium appearance-none", iiIdLocked ? "opacity-60 cursor-not-allowed" : "cursor-pointer", theme !== 'dark' ? "bg-slate-50 border-slate-200 focus:bg-white text-slate-900" : "bg-black/20 border-white/10 text-white", errors.property_type_id && "!border-rose-500")}
                            >
                                <option value="" disabled>Seleccione un tipo</option>
                                {propertyTypes.map((type) => (
                                    <option key={type.id} value={type.id}>{type.name}</option>
                                ))}
                            </select>
                            {iiIdLocked
                                ? <p className="text-xs text-slate-400 mt-1 font-medium">No editable. Duplica la propiedad para cambiar el tipo.</p>
                                : errors.property_type_id && <p className="text-xs text-rose-500 mt-1 font-medium">{errors.property_type_id}</p>}
                        </div>
                        <div data-field="price">
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-xs font-bold uppercase tracking-widest text-slate-500">Precio <span className="text-rose-500">*</span></label>
                                {/* Currency the asking price is fixed in. Hidden while no
                                    Banguat rate is available (GTQ entry needs the USD mirror). */}
                                {gtqRate && (
                                    <div role="group" aria-label="Moneda del precio" className={clsx("flex items-center p-0.5 rounded-lg border", theme !== 'dark' ? "bg-slate-100 border-slate-200" : "bg-black/20 border-white/10")}>
                                        {(['USD', 'GTQ'] as const).map(c => (
                                            <button
                                                key={c}
                                                type="button"
                                                aria-pressed={(formData.currency ?? 'USD') === c}
                                                onClick={() => {
                                                    if ((formData.currency ?? 'USD') === c) return;
                                                    if (c === 'GTQ') {
                                                        // Preserve meaning: prefill the Q amount from the USD price.
                                                        setFormData({ ...formData, currency: 'GTQ', price_gtq: formData.price ? Math.round(formData.price * gtqRate) : 0 });
                                                    } else {
                                                        // price already holds the USD mirror — it becomes the fixed amount.
                                                        setFormData({ ...formData, currency: 'USD', price_gtq: null });
                                                    }
                                                }}
                                                className={clsx(
                                                    "px-2 py-0.5 rounded-md text-xs font-black transition-all",
                                                    (formData.currency ?? 'USD') === c
                                                        ? "bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-sm"
                                                        : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                                                )}
                                            >
                                                {c === 'USD' ? '$' : 'Q'}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {(() => {
                                const isGtq = formData.currency === 'GTQ';
                                const amount = isGtq ? formData.price_gtq : formData.price;
                                return (
                                    <>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                                <span className="text-slate-400 font-bold">{isGtq ? 'Q' : '$'}</span>
                                            </div>
                                            <input
                                                type="text"
                                                placeholder={isGtq ? '1,900,000' : '250,000'}
                                                value={amount ? amount.toLocaleString('en-US') : ''}
                                                onChange={(e) => {
                                                    const rawValue = e.target.value.replace(/,/g, '');
                                                    if (rawValue === '' || !isNaN(Number(rawValue))) {
                                                        const n = rawValue === '' ? 0 : Number(rawValue);
                                                        if (isGtq) {
                                                            // Q is the committed amount; keep the USD mirror in sync.
                                                            setFormData({ ...formData, price_gtq: n, price: gtqRate ? Math.round(n / gtqRate) : 0 });
                                                        } else {
                                                            setFormData({ ...formData, price: n, price_gtq: null });
                                                        }
                                                    }
                                                }}
                                                className={clsx("w-full pl-10 pr-4 py-3 rounded-xl border focus:outline-none focus:border-indigo-500 transition-colors font-bold", theme !== 'dark' ? "bg-slate-50 border-slate-200 text-slate-900" : "bg-black/20 border-white/10 text-white", errors.price && "!border-rose-500")}
                                            />
                                        </div>
                                        {gtqRate && (amount ?? 0) > 0 && (
                                            <p className="text-xs text-slate-400 mt-1 font-medium">
                                                ≈ {isGtq ? formatUsd((formData.price_gtq ?? 0) / gtqRate) : formatGtq((formData.price ?? 0) * gtqRate)} · Tipo de cambio Banguat Q{gtqRate.toFixed(2)}
                                            </p>
                                        )}
                                    </>
                                );
                            })()}
                            {errors.price && <p className="text-xs text-rose-500 mt-1 font-medium">{errors.price}</p>}
                        </div>
                        <div>
                            <label className="block text-xs font-bold mb-2 uppercase tracking-widest text-slate-500">Mantenimiento (Opcional)</label>
                            <div className="space-y-3">
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <span className="text-secondary font-medium">Q</span>
                                    </div>
                                    <input
                                        type="text"
                                        disabled={formData.maintenance_included}
                                        value={formData.maintenance ? formData.maintenance.toLocaleString('en-US') : ''}
                                        onChange={(e) => {
                                            const rawValue = e.target.value.replace(/,/g, '');
                                            if (rawValue === '' || !isNaN(Number(rawValue))) {
                                                setFormData({ ...formData, maintenance: rawValue === '' ? undefined : Number(rawValue) });
                                            }
                                        }}
                                        className={clsx("w-full pl-10 pr-4 py-3 rounded-xl border focus:outline-none focus:border-indigo-500 transition-colors font-medium", 
                                            theme !== 'dark' ? "bg-slate-50 border-slate-200 text-slate-900 focus:bg-white" : "bg-black/20 border-white/10 text-white",
                                            formData.maintenance_included && "opacity-50 cursor-not-allowed"
                                        )}
                                        placeholder="0.00"
                                    />
                                </div>
                                <label className="flex items-center gap-2 cursor-pointer group">
                                    <div className="relative flex items-center justify-center w-5 h-5 rounded border border-white/20 bg-black/20 group-hover:border-indigo-500 transition-colors">
                                        <input
                                            type="checkbox"
                                            checked={formData.maintenance_included || false}
                                            onChange={(e) => {
                                                setFormData({ 
                                                    ...formData, 
                                                    maintenance_included: e.target.checked,
                                                    ...(e.target.checked ? { maintenance: undefined } : {})
                                                });
                                            }}
                                            className="absolute opacity-0 w-full h-full cursor-pointer"
                                        />
                                        {formData.maintenance_included && <Check size={14} className="text-indigo-400" />}
                                    </div>
                                    <span className="text-sm text-secondary group-hover:text-primary transition-colors">Mantenimiento Incluido en Precio</span>
                                </label>
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-bold mb-2 uppercase tracking-widest text-slate-500" title="Impuesto Único Sobre Inmuebles">IUSI <span className="normal-case opacity-50 font-normal">(Opcional)</span></label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <span className="text-secondary font-medium">Q</span>
                                </div>
                                <input
                                    type="text"
                                    value={formData.iusi ? formData.iusi.toLocaleString('en-US') : ''}
                                    onChange={(e) => {
                                        const rawValue = e.target.value.replace(/,/g, '');
                                        if (rawValue === '' || !isNaN(Number(rawValue))) {
                                            setFormData({ ...formData, iusi: rawValue === '' ? undefined : Number(rawValue) });
                                        }
                                    }}
                                    className={clsx("w-full pl-10 pr-4 py-3 rounded-xl border focus:outline-none focus:border-indigo-500 transition-colors font-medium",
                                        theme !== 'dark' ? "bg-slate-50 border-slate-200 text-slate-900 focus:bg-white" : "bg-black/20 border-white/10 text-white"
                                    )}
                                    placeholder="0.00"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-bold mb-2 uppercase tracking-widest text-slate-500">Etiqueta de Precio <span className="normal-case opacity-50 font-normal">(Opcional)</span></label>
                            <input
                                type="text"
                                placeholder="Ej: Negociables, al mes, última unidad, etc."
                                value={formData.price_label || ''}
                                onChange={(e) => setFormData({ ...formData, price_label: e.target.value })}
                                className={clsx("w-full px-4 py-3 rounded-xl border focus:outline-none focus:border-indigo-500 transition-colors font-medium", theme !== 'dark' ? "bg-slate-50 border-slate-200 focus:bg-white text-slate-900" : "bg-black/20 border-white/10 text-white")}
                            />
                        </div>
                        <div data-field="status">
                            <label className="block text-xs font-bold mb-2 uppercase tracking-widest text-slate-500">Estado en Plataforma <span className="text-rose-500">*</span></label>
                            <select
                                value={formData.status}
                                onChange={(e) => setFormData({ ...formData, status: e.target.value as string })}
                                className={clsx("w-full px-4 py-3 rounded-xl border focus:outline-none focus:border-indigo-500 transition-colors font-medium cursor-pointer appearance-none", theme !== 'dark' ? "bg-slate-50 border-slate-200 focus:bg-white text-slate-900" : "bg-black/20 border-white/10 text-white", errors.status && "!border-rose-500")}
                            >
                                <option value="Disponible">Disponible</option>
                                <option value="Con Reserva">Con Reserva</option>
                                <option value="Vendido">Vendido</option>
                                <option value="Alquilado">Alquilado</option>
                                <option value="Fuera de mercado">Fuera de mercado</option>
                            </select>
                            {errors.status && <p className="text-xs text-rose-500 mt-1 font-medium">{errors.status}</p>}
                        </div>
                    </div>
                </div>



                {/* ─ 4. Ubicación y Especificaciones ─ */}
                <div className={clsx("stagger-item p-6 sm:p-8 rounded-2xl border shadow-sm", theme !== 'dark' ? 'bg-white border-slate-200' : 'bg-slate-900 border-white/10')}>
                    <div className="flex items-center gap-3 mb-8">
                        <div className={clsx("p-2.5 rounded-xl", theme !== 'dark' ? "bg-indigo-50 text-indigo-600" : "bg-indigo-500/10 text-indigo-400")}>
                            <MapPin size={20} />
                        </div>
                        <div>
                            <h3 className={clsx("text-lg font-black", theme !== 'dark' ? 'text-slate-800' : 'text-white')}>Ubicación y Detalles Técnicos</h3>
                            <p className="text-xs text-slate-500 font-medium mt-0.5">Metraje, cantidad de ambientes y área geográfica</p>
                        </div>
                    </div>

                    <div className="space-y-8">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                            <div className="flex flex-col gap-1.5 focus-within:text-indigo-500">
                                <span className="text-xs font-bold uppercase tracking-widest text-slate-500 transition-colors">Habitaciones</span>
                                <select value={formData.bedrooms || 0} onChange={(e) => { const n = Number(e.target.value); setFormData({ ...formData, bedrooms: n, bedrooms_plus: n >= 7 }); }} className={clsx("w-full px-4 py-3 rounded-xl border text-center font-bold text-lg focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all appearance-none cursor-pointer", theme !== 'dark' ? "bg-slate-50 border-slate-200 text-slate-900" : "bg-black/20 border-white/10 text-white")}>
                                    <option value={0}>0</option>
                                    {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n}</option>)}
                                    <option value={7}>7+</option>
                                </select>
                            </div>
                            <div className="flex flex-col gap-1.5 focus-within:text-indigo-500">
                                <span className="text-xs font-bold uppercase tracking-widest text-slate-500 transition-colors">Baños</span>
                                <select value={formData.bathrooms || 0} onChange={(e) => { const n = Number(e.target.value); setFormData({ ...formData, bathrooms: n, bathrooms_plus: n >= 7 }); }} className={clsx("w-full px-4 py-3 rounded-xl border text-center font-bold text-lg focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all appearance-none cursor-pointer", theme !== 'dark' ? "bg-slate-50 border-slate-200 text-slate-900" : "bg-black/20 border-white/10 text-white")}>
                                    <option value={0}>0</option>
                                    {[0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5].map(n => <option key={n} value={n}>{n}</option>)}
                                    <option value={7}>7+</option>
                                </select>
                            </div>
                            <div className="flex flex-col gap-1.5 focus-within:text-indigo-500">
                                <span className="text-xs font-bold uppercase tracking-widest text-slate-500 transition-colors">Parqueos</span>
                                <select value={formData.parking || 0} onChange={(e) => { const n = Number(e.target.value); setFormData({ ...formData, parking: n, parking_plus: n >= 7 }); }} className={clsx("w-full px-4 py-3 rounded-xl border text-center font-bold text-lg focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all appearance-none cursor-pointer", theme !== 'dark' ? "bg-slate-50 border-slate-200 text-slate-900" : "bg-black/20 border-white/10 text-white")}>
                                    <option value={0}>0</option>
                                    {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n}</option>)}
                                    <option value={7}>7+</option>
                                </select>
                            </div>
                            <div className="flex flex-col gap-1.5 focus-within:text-indigo-500">
                                <span className="text-xs font-bold uppercase tracking-widest text-slate-500 transition-colors" title="Área de Construcción">Construcción (m²)</span>
                                <input min={0} type="number" placeholder="0" value={formData.built_up_area || ''} onChange={(e) => setFormData({ ...formData, built_up_area: Number(e.target.value) })} className={clsx("w-full px-4 py-3 rounded-xl border text-center font-bold text-lg focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all", theme !== 'dark' ? "bg-slate-50 border-slate-200 text-slate-900" : "bg-black/20 border-white/10 text-white")} />
                                <span className="text-[10px] leading-tight text-slate-400 font-medium normal-case">No incluir parqueos ni otras áreas</span>
                            </div>
                            <div className="flex flex-col gap-1.5 focus-within:text-indigo-500">
                                <span className="text-xs font-bold uppercase tracking-widest text-slate-500 transition-colors" title="Área de Terreno en metros cuadrados — se convierte a varas²">Terreno (m²)</span>
                                <input min={0} type="number" placeholder="0" value={lotM2} onChange={(e) => {
                                    const raw = e.target.value; setLotM2(raw);
                                    const n = parseFloat(raw);
                                    const v = raw === '' || isNaN(n) ? undefined : round2(n / M2_PER_VARA2);
                                    setLotVaras(v === undefined ? '' : String(v));
                                    setFormData(prev => ({ ...prev, lot_size_varas: v } as any));
                                }} className={clsx("w-full px-4 py-3 rounded-xl border text-center font-bold text-lg focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all", theme !== 'dark' ? "bg-slate-50 border-slate-200 text-slate-900" : "bg-black/20 border-white/10 text-white")} />
                            </div>
                            <div className="flex flex-col gap-1.5 focus-within:text-indigo-500">
                                <span className="text-xs font-bold uppercase tracking-widest text-slate-500 transition-colors" title="Área de Terreno o Lote — valor guardado (varas²)">Terreno (v²)</span>
                                <input min={0} type="number" placeholder="0" value={lotVaras} onChange={(e) => {
                                    const raw = e.target.value; setLotVaras(raw);
                                    const n = parseFloat(raw);
                                    setLotM2(raw === '' || isNaN(n) ? '' : String(round2(n * M2_PER_VARA2)));
                                    setFormData(prev => ({ ...prev, lot_size_varas: isNaN(n) ? undefined : n } as any));
                                }} className={clsx("w-full px-4 py-3 rounded-xl border text-center font-bold text-lg focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all", theme !== 'dark' ? "bg-slate-50 border-slate-200 text-slate-900" : "bg-black/20 border-white/10 text-white")} />
                            </div>
                            <div className="flex flex-col gap-1.5 focus-within:text-indigo-500">
                                <span className="text-xs font-bold uppercase tracking-widest text-slate-500 transition-colors" title="Frente del terreno en metros">Frente (m)</span>
                                <input min={0} type="number" placeholder="0" value={formData.frente || ''} onChange={(e) => setFormData({ ...formData, frente: e.target.value === '' ? undefined : Number(e.target.value) })} className={clsx("w-full px-4 py-3 rounded-xl border text-center font-bold text-lg focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all", theme !== 'dark' ? "bg-slate-50 border-slate-200 text-slate-900" : "bg-black/20 border-white/10 text-white")} />
                            </div>
                            <div className="flex flex-col gap-1.5 focus-within:text-indigo-500">
                                <span className="text-xs font-bold uppercase tracking-widest text-slate-500 transition-colors" title="Fondo del terreno en metros">Fondo (m)</span>
                                <input min={0} type="number" placeholder="0" value={formData.fondo || ''} onChange={(e) => setFormData({ ...formData, fondo: e.target.value === '' ? undefined : Number(e.target.value) })} className={clsx("w-full px-4 py-3 rounded-xl border text-center font-bold text-lg focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all", theme !== 'dark' ? "bg-slate-50 border-slate-200 text-slate-900" : "bg-black/20 border-white/10 text-white")} />
                            </div>
                            <div className="flex flex-col gap-1.5 focus-within:text-indigo-500">
                                <span className="text-xs font-bold uppercase tracking-widest text-slate-500 transition-colors">Año Const.</span>
                                <input type="number" placeholder="Ej: 2022" value={(formData as any).year_built || ''} onChange={(e) => setFormData({ ...formData, year_built: Number(e.target.value) } as any)} className={clsx("w-full px-4 py-3 rounded-xl border text-center font-bold text-lg focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all", theme !== 'dark' ? "bg-slate-50 border-slate-200 text-slate-900" : "bg-black/20 border-white/10 text-white")} />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div data-field="department">
                                <label className="block text-xs font-bold mb-2 uppercase tracking-widest text-slate-500">Departamento <span className="text-rose-500">*</span></label>
                                <select
                                    value={formData.location?.state || ''}
                                    onChange={(e) => {
                                        const name = e.target.value;
                                        const dept = departments.find(d => d.name === name);
                                        // Coordinates: prefer DB-stored, fallback to hardcoded lookup.
                                        const lat = dept?.latitude != null ? Number(dept.latitude) : (DEPT_COORDS[name]?.[0] ?? null);
                                        const lng = dept?.longitude != null ? Number(dept.longitude) : (DEPT_COORDS[name]?.[1] ?? null);
                                        setFormData({
                                            ...formData,
                                            department_id: dept?.id,
                                            location: { ...formData.location!, state: name, sector: '', city: '' },
                                            ...(lat != null && lng != null ? { latitude: String(lat), longitude: String(lng) } : {})
                                        });
                                        focusMap({ lat, lng, query: lat == null ? `${name}, Guatemala` : null, zoom: ZOOM.department });
                                    }}
                                    className={clsx("w-full px-4 py-3 rounded-xl border focus:outline-none focus:border-indigo-500 transition-colors font-medium cursor-pointer appearance-none", theme !== 'dark' ? "bg-slate-50 border-slate-200 text-slate-900 focus:bg-white" : "bg-black/20 border-white/10 text-white", errors.department && "!border-rose-500")}
                                >
                                    <option value="" disabled>Seleccione un departamento</option>
                                    {departments.map((dept) => (
                                        <option key={dept.id} value={dept.name}>{dept.name}</option>
                                    ))}
                                </select>
                                {errors.department && <p className="text-xs text-rose-500 mt-1 font-medium">{errors.department}</p>}
                            </div>
                            <div data-field="sector">
                                <label className="block text-xs font-bold mb-2 uppercase tracking-widest text-slate-500">Sector <span className="text-rose-500">*</span></label>
                                <select
                                    value={formData.location?.sector || ''}
                                    onChange={(e) => {
                                        const name = e.target.value;
                                        const sector = sectors.find(s => s.name === name);
                                        const lat = sector?.latitude != null ? Number(sector.latitude) : null;
                                        const lng = sector?.longitude != null ? Number(sector.longitude) : null;
                                        const own = lat != null && lng != null;
                                        setFormData({
                                            ...formData,
                                            city_id: sector?.id,
                                            sector_id: undefined,
                                            location: { ...formData.location!, sector: name, city: '' },
                                            ...(own ? { latitude: String(lat), longitude: String(lng) } : {})
                                        });
                                        // No stored coords → keep current (department) coords as the
                                        // immediate target and geocode the sector for a precise pin.
                                        focusMap({
                                            lat: own ? lat : (formData.latitude ? Number(formData.latitude) : null),
                                            lng: own ? lng : (formData.longitude ? Number(formData.longitude) : null),
                                            query: own ? null : `${name}, ${formData.location?.state || ''}, Guatemala`,
                                            zoom: ZOOM.sector,
                                        });
                                    }}
                                    disabled={sectors.length === 0}
                                    className={clsx("w-full px-4 py-3 rounded-xl border focus:outline-none focus:border-indigo-500 transition-colors font-medium cursor-pointer appearance-none disabled:opacity-50 disabled:cursor-not-allowed", theme !== 'dark' ? "bg-slate-50 border-slate-200 text-slate-900 focus:bg-white" : "bg-black/20 border-white/10 text-white", errors.sector && "!border-rose-500")}
                                >
                                    <option value="" disabled>Seleccione un sector</option>
                                    {sectors.map((sector) => (
                                        <option key={sector.id} value={sector.name}>{sector.name}</option>
                                    ))}
                                </select>
                                {errors.sector && <p className="text-xs text-rose-500 mt-1 font-medium">{errors.sector}</p>}
                            </div>
                            <div>
                                <label className="block text-xs font-bold mb-2 uppercase tracking-widest text-slate-500">Sub-Sector / Proyecto</label>
                                <select
                                    value={formData.location?.city || ''}
                                    onChange={(e) => {
                                        const name = e.target.value;
                                        const city = cities.find(c => c.name === name);
                                        const lat = city?.latitude != null ? Number(city.latitude) : null;
                                        const lng = city?.longitude != null ? Number(city.longitude) : null;
                                        const own = lat != null && lng != null;
                                        setFormData({
                                            ...formData,
                                            sector_id: city?.id,
                                            location: { ...formData.location!, city: name },
                                            ...(own ? { latitude: String(lat), longitude: String(lng) } : {})
                                        });
                                        // No stored coords → keep current (sector/department) coords and
                                        // geocode the sub-sector within its sector for a precise pin.
                                        focusMap({
                                            lat: own ? lat : (formData.latitude ? Number(formData.latitude) : null),
                                            lng: own ? lng : (formData.longitude ? Number(formData.longitude) : null),
                                            query: own ? null : `${name}, ${formData.location?.sector || ''}, ${formData.location?.state || ''}, Guatemala`,
                                            zoom: ZOOM.subsector,
                                        });
                                    }}
                                    disabled={cities.length === 0}
                                    className={clsx("w-full px-4 py-3 rounded-xl border focus:outline-none focus:border-indigo-500 transition-colors font-medium cursor-pointer appearance-none disabled:opacity-50 disabled:cursor-not-allowed", theme !== 'dark' ? "bg-slate-50 border-slate-200 text-slate-900 focus:bg-white" : "bg-black/20 border-white/10 text-white")}
                                >
                                    <option value="" disabled>Seleccione un sub-sector / proyecto</option>
                                    {cities.map((city) => (
                                        <option key={city.id} value={city.name}>{city.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Map Section */}
                        <div className={clsx("mt-8 pt-8 border-t", theme !== 'dark' ? "border-slate-200" : "border-white/10")}>
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-5">
                                <div>
                                    <h4 className={clsx("text-sm font-bold mb-1", theme !== 'dark' ? 'text-slate-800' : 'text-white')}>Ubicación Exacta</h4>
                                    <p className="text-xs text-slate-500">Arrastra el pin para ajustar. Al seleccionar tu sector se moverá automáticamente.</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <label className="flex items-center gap-2.5 cursor-pointer group select-none">
                                        <div
                                            className="relative"
                                            style={{ width: 44, height: 24 }}
                                        >
                                            <input
                                                type="checkbox"
                                                className="sr-only"
                                                checked={formData.show_exact_location ?? false}
                                                onChange={(e) => setFormData({ ...formData, show_exact_location: e.target.checked })}
                                            />
                                            {/* Track */}
                                            <div
                                                style={{
                                                    width: 44,
                                                    height: 24,
                                                    borderRadius: 9999,
                                                    backgroundColor: (formData.show_exact_location ?? false) ? 'var(--accent-500, #3b82f6)' : (theme === 'dark' ? 'rgba(255,255,255,0.06)' : '#e2e8f0'),
                                                    border: (formData.show_exact_location ?? false) ? 'none' : (theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid #cbd5e1'),
                                                    transition: 'background-color 0.3s ease',
                                                }}
                                            />
                                            {/* Knob */}
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    top: 3,
                                                    left: (formData.show_exact_location ?? false) ? 23 : 3,
                                                    width: 18,
                                                    height: 18,
                                                    borderRadius: 9999,
                                                    backgroundColor: '#fff',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                                                    transition: 'left 0.3s ease',
                                                }}
                                            />
                                        </div>
                                        <span className={clsx(
                                            "text-xs font-bold transition-colors whitespace-nowrap",
                                            theme !== 'dark' ? "text-slate-600 group-hover:text-slate-900" : "text-slate-400 group-hover:text-white"
                                        )}>
                                            Mostrar al público
                                        </span>
                                    </label>
                                </div>
                            </div>
                            
                            <div className={clsx("p-2 rounded-xl border relative z-0", theme !== 'dark' ? "bg-slate-50 border-slate-200" : "bg-black/20 border-white/10")}>
                                <LocationMap
                                    lat={formData.latitude}
                                    lng={formData.longitude}
                                    onCoordinatesChange={handleCoordinatesChange}
                                    focus={mapFocus}
                                    height={350}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* ─ Puntos de Interés ─ */}
                <div className={clsx("stagger-item p-6 sm:p-8 rounded-2xl border shadow-sm", theme !== 'dark' ? 'bg-white border-slate-200' : 'bg-slate-900 border-white/10')}>
                    <div className="flex items-center gap-3 mb-8">
                        <div className={clsx("p-2.5 rounded-xl", theme !== 'dark' ? "bg-indigo-50 text-indigo-600" : "bg-indigo-500/10 text-indigo-400")}>
                            <MapPin size={20} />
                        </div>
                        <div>
                            <h3 className={clsx("text-lg font-black", theme !== 'dark' ? 'text-slate-800' : 'text-white')}>Lugares de Interés Cercanos</h3>
                            <p className="text-xs text-slate-500 font-medium mt-0.5">Agrega puntos de interés cercanos (ej. Centro Comercial, 10 min en carro)</p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        {(formData.points_of_interest || []).map((poi, idx) => (
                            <div key={idx} className={clsx("flex flex-col sm:flex-row gap-3 p-4 rounded-xl border", theme !== 'dark' ? "bg-slate-50 border-slate-200" : "bg-white/5 border-white/10")}>
                                <div className="flex-1 flex flex-col gap-1.5 focus-within:text-indigo-500">
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 transition-colors">Nombre del lugar</span>
                                    <input
                                        type="text"
                                        placeholder="ej. Cayalá"
                                        value={poi.name}
                                        onChange={(e) => {
                                            const newPois = [...(formData.points_of_interest || [])];
                                            newPois[idx].name = e.target.value;
                                            setFormData({ ...formData, points_of_interest: newPois });
                                        }}
                                        className={clsx("w-full px-4 py-3 rounded-xl border focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all font-medium", theme !== 'dark' ? "bg-white border-slate-200 text-slate-900" : "bg-black/20 border-white/10 text-white")}
                                    />
                                </div>
                                <div className="flex-1 flex flex-col gap-1.5 focus-within:text-indigo-500">
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 transition-colors">Distancia / Tiempo</span>
                                    <input
                                        type="text"
                                        placeholder="ej. 10 min en carro"
                                        value={poi.description}
                                        onChange={(e) => {
                                            const newPois = [...(formData.points_of_interest || [])];
                                            newPois[idx].description = e.target.value;
                                            setFormData({ ...formData, points_of_interest: newPois });
                                        }}
                                        className={clsx("w-full px-4 py-3 rounded-xl border focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all font-medium", theme !== 'dark' ? "bg-white border-slate-200 text-slate-900" : "bg-black/20 border-white/10 text-white")}
                                    />
                                </div>
                                <div className="flex flex-col justify-end pb-1 sm:pb-0">
                                    <button
                                        onClick={() => {
                                            const newPois = [...(formData.points_of_interest || [])];
                                            newPois.splice(idx, 1);
                                            setFormData({ ...formData, points_of_interest: newPois });
                                        }}
                                        className={clsx("p-3.5 rounded-xl flex items-center justify-center transition-colors w-full sm:w-auto mt-[22px]", theme !== 'dark' ? "text-red-500 hover:bg-red-50 bg-white border border-red-100" : "text-red-400 hover:bg-red-500/10 bg-black/20 border border-red-900/30")}
                                        title="Quitar"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            </div>
                        ))}

                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                setFormData({
                                    ...formData,
                                    points_of_interest: [...(formData.points_of_interest || []), { name: '', description: '' }]
                                });
                            }}
                            className={clsx("w-full py-4 rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2 transition-all font-bold text-sm", theme !== 'dark' ? "border-slate-300 text-slate-500 hover:border-indigo-500 hover:text-indigo-600 hover:bg-indigo-50/50" : "border-white/10 text-slate-400 hover:border-indigo-500 hover:text-indigo-400 bg-white/5")}
                        >
                            <Plus size={20} />
                            Añadir Nuevo Lugar
                        </button>
                    </div>
                </div>

                {/* ─ 5. Amenidades ─ */}
                <div className={clsx("stagger-item p-6 sm:p-8 rounded-2xl border shadow-sm", theme !== 'dark' ? 'bg-white border-slate-200' : 'bg-slate-900 border-white/10')}>
                    <div className="flex items-center gap-3 mb-8">
                        <div className={clsx("p-2.5 rounded-xl", theme !== 'dark' ? "bg-indigo-50 text-indigo-600" : "bg-indigo-500/10 text-indigo-400")}>
                            <Sparkles size={20} />
                        </div>
                        <div>
                            <h3 className={clsx("text-lg font-black", theme !== 'dark' ? 'text-slate-800' : 'text-white')}>Amenidades del Inmueble</h3>
                            <p className="text-xs text-slate-500 font-medium mt-0.5">Selecciona las instalaciones adicionales que ofrece el complejo</p>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2.5">
                        {availableAmenities.length > 0 ? (
                            availableAmenities.map(amenity => {
                                const isSelected = isAmenitySelected(formData.amenities ?? [], amenity.name);
                                return (
                                    <button
                                        key={amenity.id}
                                        onClick={() => setFormData({
                                            ...formData,
                                            amenities: toggleAmenity(formData.amenities ?? [], amenity.name),
                                        })}
                                        className={clsx(
                                            "px-4 py-2 rounded-xl text-sm font-bold transition-all border outline-none select-none",
                                            isSelected
                                                ? "bg-indigo-600 text-white border-indigo-600 shadow-lg shadow-indigo-600/30 scale-105"
                                                : theme !== 'dark'
                                                    ? "bg-slate-50 border-slate-200 text-slate-600 hover:border-indigo-300 hover:bg-white"
                                                    : "bg-black/20 border-white/10 text-slate-400 hover:border-indigo-500/50 hover:bg-white/5 hover:text-white"
                                        )}
                                    >
                                        {amenity.name}
                                    </button>
                                );
                            })
                        ) : (
                            <div className="flex items-center gap-2 text-slate-500 font-medium py-2">
                                <div className="w-4 h-4 rounded-full border-2 border-indigo-500/30 border-t-indigo-500 animate-spin" />
                                <span className="text-sm">Cargando amenidades...</span>
                            </div>
                        )}
                    </div>
                    {/* Etiquetas — global marketing tags (Preventa, Airbnb, ...) shown
                        as portal filters/badges. Optional multi-select. */}
                    <div className="mt-8">
                        <div className="mb-4">
                            <h4 className={clsx("text-sm font-black", theme !== 'dark' ? 'text-slate-800' : 'text-white')}>Etiquetas</h4>
                            <p className="text-xs text-slate-500 font-medium mt-0.5">Opcionales — resaltan la propiedad y sirven como filtros en los portales</p>
                        </div>
                        <div className="flex flex-wrap gap-2.5">
                            {priceTags.map(tag => {
                                const isSelected = selectedTagIds.includes(tag.id);
                                return (
                                    <button
                                        key={tag.id}
                                        onClick={() => setSelectedTagIds(prev => isSelected ? prev.filter(id => id !== tag.id) : [...prev, tag.id])}
                                        className={clsx(
                                            "px-4 py-2 rounded-xl text-sm font-bold transition-all border outline-none select-none",
                                            isSelected
                                                ? "text-white scale-105 shadow-lg"
                                                : theme !== 'dark'
                                                    ? "bg-slate-50 border-slate-200 text-slate-600 hover:border-indigo-300 hover:bg-white"
                                                    : "bg-black/20 border-white/10 text-slate-400 hover:border-indigo-500/50 hover:bg-white/5 hover:text-white"
                                        )}
                                        style={isSelected ? { backgroundColor: tag.color || '#6366f1', borderColor: tag.color || '#6366f1' } : undefined}
                                    >
                                        {tag.name}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* ─ 6. Visibilidad y Red ─ */}
                <div data-tour="visibilidad" className={clsx("stagger-item p-6 sm:p-8 rounded-2xl border shadow-sm", theme !== 'dark' ? 'bg-white border-slate-200' : 'bg-slate-900 border-white/10')}>
                    <div className="flex items-center gap-3 mb-8">
                        <div className={clsx("p-2.5 rounded-xl", theme !== 'dark' ? "bg-indigo-50 text-indigo-600" : "bg-indigo-500/10 text-indigo-400")}>
                            <Globe size={20} />
                        </div>
                        <div>
                            <h3 className={clsx("text-lg font-black", theme !== 'dark' ? 'text-slate-800' : 'text-white')}>Visibilidad y Red</h3>
                            <p className="text-xs text-slate-500 font-medium mt-0.5">Controles para publicar y compartir la propiedad</p>
                        </div>
                    </div>

                    {/* Activar Propiedad — only when editing a draft. Gates publish/share
                        below so a draft can't be left in an inconsistent "published draft"
                        state. */}
                    {isEditingDraft && (
                        <div
                            onClick={() => setActivateProperty(prev => !prev)}
                            className={clsx(
                                "flex items-center justify-between p-5 rounded-2xl border cursor-pointer transition-all mb-6",
                                activateProperty
                                    ? "bg-amber-500/10 border-amber-500/30"
                                    : theme !== 'dark' ? "bg-slate-50 border-slate-200" : "bg-white/5 border-white/10"
                            )}
                        >
                            <div className="flex items-center gap-4">
                                <div className={clsx(
                                    "p-3 rounded-full transition-colors",
                                    activateProperty
                                        ? "bg-amber-500 text-white"
                                        : theme !== 'dark' ? "bg-slate-200 text-slate-500" : "bg-slate-800 text-slate-400"
                                )}>
                                    <Power size={20} />
                                </div>
                                <div>
                                    <h4 className={clsx("font-bold", theme !== 'dark' ? 'text-slate-800' : 'text-white')}>Activar Propiedad</h4>
                                    <p className="text-xs text-slate-500 font-medium mt-1">
                                        {activateProperty
                                            ? 'Al guardar, dejará de ser borrador y aparecerá en Activas'
                                            : 'Se mantendrá como borrador (no visible en Activas)'}
                                    </p>
                                </div>
                            </div>
                            <div
                                className={clsx(
                                    "rounded-full transition-colors shrink-0",
                                    activateProperty ? "bg-amber-500" : theme !== 'dark' ? "bg-slate-300" : "bg-slate-700"
                                )}
                                style={{ position: 'relative', width: 48, height: 28 }}
                            >
                                <div
                                    className="rounded-full bg-white shadow-sm"
                                    style={{
                                        position: 'absolute',
                                        top: 4,
                                        left: activateProperty ? 24 : 4,
                                        width: 20,
                                        height: 20,
                                        transition: 'left 300ms ease',
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    <div className={clsx(
                        "grid grid-cols-1 md:grid-cols-2 gap-6 transition-opacity",
                        isEditingDraft && !activateProperty ? "opacity-50 pointer-events-none" : ""
                    )}>
                        {/* Publicar Propiedad Toggle */}
                        <div
                            onClick={() => setPublishStatus(prev => prev === 'published' ? 'unpublished' : 'published')}
                            className={clsx(
                                "flex items-center justify-between p-5 rounded-2xl border cursor-pointer transition-all",
                                publishStatus === 'published'
                                    ? "bg-indigo-500/10 border-indigo-500/30"
                                    : theme !== 'dark' ? "bg-slate-50 border-slate-200" : "bg-white/5 border-white/10"
                            )}
                        >
                            <div className="flex items-center gap-4">
                                <div className={clsx(
                                    "p-3 rounded-full transition-colors",
                                    publishStatus === 'published'
                                        ? "bg-indigo-500 text-white"
                                        : theme !== 'dark' ? "bg-slate-200 text-slate-500" : "bg-slate-800 text-slate-400"
                                )}>
                                    <Globe size={20} />
                                </div>
                                <div>
                                    <h4 className={clsx("font-bold", theme !== 'dark' ? 'text-slate-800' : 'text-white')}>Publicar Propiedad</h4>
                                    <p className="text-xs text-slate-500 font-medium mt-1">Hacer visible en el portal público</p>
                                </div>
                            </div>
                            {/* Switch UI */}
                            <div
                                className={clsx(
                                    "rounded-full transition-colors shrink-0",
                                    publishStatus === 'published' ? "bg-indigo-500" : theme !== 'dark' ? "bg-slate-300" : "bg-slate-700"
                                )}
                                style={{ position: 'relative', width: 48, height: 28 }}
                            >
                                <div
                                    className="rounded-full bg-white shadow-sm"
                                    style={{
                                        position: 'absolute',
                                        top: 4,
                                        left: publishStatus === 'published' ? 24 : 4,
                                        width: 20,
                                        height: 20,
                                        transition: 'left 300ms ease',
                                    }}
                                />
                            </div>
                        </div>

                        {/* Compartir en Red Toggle */}
                        <div
                            onClick={() => setShareStatus(prev => prev === 'shared' ? 'not_shared' : 'shared')}
                            className={clsx(
                                "flex items-center justify-between p-5 rounded-2xl border cursor-pointer transition-all",
                                shareStatus === 'shared'
                                    ? "bg-emerald-500/10 border-emerald-500/30"
                                    : theme !== 'dark' ? "bg-slate-50 border-slate-200" : "bg-white/5 border-white/10"
                            )}
                        >
                            <div className="flex items-center gap-4">
                                <div className={clsx(
                                    "p-3 rounded-full transition-colors",
                                    shareStatus === 'shared'
                                        ? "bg-emerald-500 text-white"
                                        : theme !== 'dark' ? "bg-slate-200 text-slate-500" : "bg-slate-800 text-slate-400"
                                )}>
                                    <Network size={20} />
                                </div>
                                <div>
                                    <h4 className={clsx("font-bold", theme !== 'dark' ? 'text-slate-800' : 'text-white')}>Compartir en Red</h4>
                                    <p className="text-xs text-slate-500 font-medium mt-1">Permitir co-brokerage con colegas</p>
                                </div>
                            </div>
                            {/* Switch UI */}
                            <div
                                className={clsx(
                                    "rounded-full transition-colors shrink-0",
                                    shareStatus === 'shared' ? "bg-emerald-500" : theme !== 'dark' ? "bg-slate-300" : "bg-slate-700"
                                )}
                                style={{ position: 'relative', width: 48, height: 28 }}
                            >
                                <div
                                    className="rounded-full bg-white shadow-sm"
                                    style={{
                                        position: 'absolute',
                                        top: 4,
                                        left: shareStatus === 'shared' ? 24 : 4,
                                        width: 20,
                                        height: 20,
                                        transition: 'left 300ms ease',
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* ─ 6. Detalles Adicionales no públicos ─ */}
                <div className={clsx("stagger-item p-6 sm:p-8 rounded-2xl border mb-6 shadow-sm", theme !== 'dark' ? 'bg-white border-slate-200' : 'bg-slate-900 border-white/10')}>
                    <div className="flex items-center gap-3 mb-8">
                        <div className={clsx("p-2.5 rounded-xl", theme !== 'dark' ? "bg-indigo-50 text-indigo-600" : "bg-indigo-500/10 text-indigo-400")}>
                            <User size={20} />
                        </div>
                        <div>
                            <h3 className={clsx("text-lg font-black", theme !== 'dark' ? 'text-slate-800' : 'text-white')}>Detalles Adicionales no públicos</h3>
                            <p className="text-xs text-slate-500 font-medium mt-0.5">Gestión interna, propietarios y documentación confidencial</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                        <div data-field="agent_id">
                            <label className="block text-xs font-bold mb-2 uppercase tracking-widest text-slate-500">Agente Responsable <span className="text-rose-500">*</span></label>
                            {/* Bind by agent_id (UUID) so the display always matches the
                                persisted assignment. Previously the select matched options
                                by agent.name, which silently fell through to the first
                                option (alphabetically) whenever formData.agent.name wasn't
                                hydrated — e.g. when the edit form loaded via the direct
                                fetch path that doesn't join profiles. That caused the form
                                to visually show "someone else" for the correct agent_id. */}
                            <select
                                disabled={profile?.role !== 'agency_admin'}
                                value={formData.agent_id || ''}
                                onChange={(e) => {
                                    const newId = e.target.value;
                                    const agent = agents.find((a: any) => a.id === newId);
                                    setFormData({
                                        ...formData,
                                        agent_id: newId,
                                        agent: agent ? {
                                            ...formData.agent!,
                                            name: agent.name,
                                            avatar: agent.avatar || formData.agent?.avatar || '',
                                            role: 'Agente Asociado'
                                        } : formData.agent,
                                    });
                                }}
                                className={clsx(
                                    "w-full px-4 py-3 rounded-xl border focus:outline-none focus:border-indigo-500 transition-colors font-medium appearance-none",
                                    theme !== 'dark' ? "bg-slate-50 border-slate-200 text-slate-900 focus:bg-white" : "bg-black/20 border-white/10 text-white",
                                    profile?.role !== 'agency_admin' ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
                                    errors.agent_id && "!border-rose-500"
                                )}
                            >
                                <option value="" disabled>Seleccione un agente</option>
                                {/* If the property's current agent_id isn't in the active
                                    list (left the agency, deactivated, etc.), show a
                                    synthetic option so the select renders the right name
                                    instead of an alphabetically-arbitrary "first option". */}
                                {formData.agent_id && !agents.some((a: any) => a.id === formData.agent_id) && (
                                    <option value={formData.agent_id}>
                                        {(formData as any).agent?.name || (formData as any).agent_name || 'Agente asignado'}
                                    </option>
                                )}
                                {agents.map((agent: any) => (
                                    <option key={agent.id} value={agent.id}>{agent.name}</option>
                                ))}
                            </select>
                            {errors.agent_id && <p className="text-xs text-rose-500 mt-1 font-medium">{errors.agent_id}</p>}
                        </div>
                        <div className="flex flex-col gap-3">
                            <div>
                                <label className="block text-xs font-bold mb-2 uppercase tracking-widest text-slate-500">Propietario / Contacto</label>
                                {/* Type-to-search combobox — the flat <select> got unusable
                                    once agencies passed a few dozen owners. Options use
                                    onMouseDown (fires before the input's blur closes the
                                    dropdown). */}
                                {formData.owner_id && formData.owner_id !== 'create_new' ? (
                                    <div className={clsx("flex items-center justify-between gap-2 w-full px-4 py-3 rounded-xl border font-medium", theme !== 'dark' ? "bg-slate-50 border-slate-200 text-slate-900" : "bg-black/20 border-white/10 text-white")}>
                                        <span className="truncate">{resolvedOwnerName || 'Propietario asignado'}</span>
                                        <button
                                            type="button"
                                            onClick={() => { setFormData({ ...formData, owner_id: '', owner: undefined }); setOwnerSearch(''); }}
                                            className="shrink-0 flex items-center gap-1 text-xs font-bold text-slate-400 hover:text-rose-500 transition-colors"
                                            title="Quitar propietario"
                                        >
                                            <X size={14} /> Quitar
                                        </button>
                                    </div>
                                ) : formData.owner_id === 'create_new' ? (
                                    <div className={clsx("flex items-center justify-between gap-2 w-full px-4 py-3 rounded-xl border font-medium", theme !== 'dark' ? "bg-indigo-50 border-indigo-200 text-indigo-700" : "bg-indigo-500/10 border-indigo-500/20 text-indigo-300")}>
                                        <span className="text-sm font-bold">Creando nuevo propietario...</span>
                                        <button
                                            type="button"
                                            onClick={() => setFormData({ ...formData, owner_id: '', owner: undefined })}
                                            className="shrink-0 text-xs font-bold text-slate-400 hover:text-rose-500 transition-colors"
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                                        <input
                                            type="text"
                                            value={ownerSearch}
                                            onChange={(e) => { setOwnerSearch(e.target.value); setOwnerDropdownOpen(true); }}
                                            onFocus={() => setOwnerDropdownOpen(true)}
                                            onBlur={() => setTimeout(() => setOwnerDropdownOpen(false), 150)}
                                            placeholder="Buscar propietario por nombre..."
                                            className={clsx("w-full pl-10 pr-4 py-3 rounded-xl border focus:outline-none focus:border-indigo-500 transition-colors font-medium", theme !== 'dark' ? "bg-slate-50 border-slate-200 text-slate-900 focus:bg-white placeholder:text-slate-400" : "bg-black/20 border-white/10 text-white placeholder:text-slate-500")}
                                        />
                                        {ownerDropdownOpen && (
                                            <div className={clsx("absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-xl border shadow-xl", theme !== 'dark' ? "bg-white border-slate-200" : "bg-[#0f172a] border-white/10")}>
                                                <button
                                                    type="button"
                                                    onMouseDown={(e) => { e.preventDefault(); setFormData({ ...formData, owner_id: 'create_new' }); setOwnerDropdownOpen(false); }}
                                                    className={clsx("w-full flex items-center gap-2 text-left px-4 py-2.5 text-sm font-bold text-indigo-500 transition-colors", theme !== 'dark' ? "hover:bg-indigo-50" : "hover:bg-indigo-500/10")}
                                                >
                                                    <Plus size={14} /> Crear nuevo propietario...
                                                </button>
                                                {filteredOwners.map((owner: any) => (
                                                    <button
                                                        key={owner.id}
                                                        type="button"
                                                        onMouseDown={(e) => { e.preventDefault(); setFormData({ ...formData, owner_id: owner.id, owner: undefined }); setOwnerSearch(''); setOwnerDropdownOpen(false); }}
                                                        className={clsx("w-full text-left px-4 py-2.5 text-sm font-medium transition-colors", theme !== 'dark' ? "text-slate-700 hover:bg-slate-50" : "text-slate-200 hover:bg-white/5")}
                                                    >
                                                        {owner.name}
                                                    </button>
                                                ))}
                                                {filteredOwners.length === 0 && (
                                                    <p className="px-4 py-2.5 text-sm text-slate-400 italic">Sin propietarios que coincidan.</p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            
                            {formData.owner_id === 'create_new' && (
                                <div className={clsx("p-4 rounded-xl border animate-in slide-in-from-top-2", theme !== 'dark' ? "bg-slate-50 border-slate-200" : "bg-white/5 border-white/10")}>
                                    <div className="flex items-center gap-2 mb-4 text-indigo-500">
                                        <User size={16} />
                                        <h4 className="text-sm font-bold">Nuevo Propietario</h4>
                                    </div>
                                    <div className="grid grid-cols-1 gap-4">
                                        <div>
                                            <input
                                                type="text"
                                                placeholder="Nombre (Ej: Juan Pérez)"
                                                value={formData.owner?.name || ''}
                                                onChange={(e) => setFormData({ ...formData, owner: { ...formData.owner, name: e.target.value } as any })}
                                                className={clsx("w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:border-indigo-500 transition-colors", theme !== 'dark' ? "bg-white border-slate-200 text-slate-900" : "bg-black/20 border-white/10 text-white")}
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <input
                                                type="tel"
                                                placeholder="Teléfono"
                                                value={formData.owner?.phone || ''}
                                                onChange={(e) => setFormData({ ...formData, owner: { ...formData.owner, phone: e.target.value } as any })}
                                                className={clsx("w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:border-indigo-500 transition-colors", theme !== 'dark' ? "bg-white border-slate-200 text-slate-900" : "bg-black/20 border-white/10 text-white")}
                                            />
                                            <input
                                                type="email"
                                                placeholder="Correo (opcional)"
                                                value={formData.owner?.email || ''}
                                                onChange={(e) => setFormData({ ...formData, owner: { ...formData.owner, email: e.target.value } as any })}
                                                className={clsx("w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:border-indigo-500 transition-colors", theme !== 'dark' ? "bg-white border-slate-200 text-slate-900" : "bg-black/20 border-white/10 text-white")}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Documentos Drag & Drop */}
                    <div className="space-y-4">
                        <label className="block text-xs font-bold uppercase tracking-widest text-slate-500">Documentación de la Propiedad (Planos, Escrituras, etc.)</label>

                        <div
                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragActiveDocs(true); }}
                            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragActiveDocs(false); }}
                            onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setDragActiveDocs(false);
                                if (e.dataTransfer.files) handleDocFiles(e.dataTransfer.files);
                            }}
                            onClick={() => docInputRef.current?.click()}
                            className={clsx(
                                "border-2 border-dashed rounded-2xl p-8 transition-all flex flex-col items-center justify-center text-center cursor-pointer",
                                dragActiveDocs
                                    ? "border-indigo-500 bg-indigo-500/10 scale-[0.99]"
                                    : theme !== 'dark'
                                        ? "border-slate-200 bg-slate-50/50 hover:border-indigo-300 hover:bg-white"
                                        : "border-white/10 bg-black/20 hover:border-white/20"
                            )}
                        >
                            <input
                                type="file"
                                ref={docInputRef}
                                className="hidden"
                                multiple
                                onChange={(e) => handleDocFiles(e.target.files)}
                            />
                            <div className={clsx(
                                "mb-3 p-3 rounded-xl",
                                theme !== 'dark' ? "bg-white shadow-sm text-indigo-600" : "bg-white/10 text-indigo-400"
                            )}>
                                <Paperclip size={24} />
                            </div>
                            <h4 className={clsx("text-sm font-bold mb-1", theme !== 'dark' ? 'text-slate-800' : 'text-white')}>
                                {dragActiveDocs ? "¡Suelta los documentos!" : "Arrastra documentos aquí"}
                            </h4>
                            <p className="text-xs text-slate-500 font-medium">PDF, DOC, DOCX, XLS, XLSX, TXT o imágenes (máx. 20 MB cada uno)</p>
                        </div>

                        {docError && (
                            <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs font-medium whitespace-pre-line">
                                {docError}
                            </div>
                        )}

                        {docsUploading && (
                            <div className="flex items-center gap-2 text-xs text-indigo-600 font-medium">
                                <Loader2 size={14} className="animate-spin" />
                                Subiendo documentos…
                            </div>
                        )}

                        {/* List of documents */}
                        {documents.length > 0 && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                                {documents.map((doc, idx) => (
                                    <div
                                        key={idx}
                                        className={clsx(
                                            "flex items-center justify-between p-3 rounded-xl border transition-all",
                                            theme !== 'dark' ? "bg-white border-slate-200" : "bg-white/5 border-white/10"
                                        )}
                                    >
                                        <div className="flex items-center gap-3 overflow-hidden">
                                            <div className="p-2 rounded-lg bg-indigo-500/10 text-indigo-500 flex-shrink-0">
                                                <FileText size={18} />
                                            </div>
                                            <div className="overflow-hidden">
                                                <p className={clsx("text-xs font-bold truncate", theme !== 'dark' ? "text-slate-700" : "text-white")}>{doc.name}</p>
                                                <p className="text-[10px] text-slate-500 font-medium">{(doc.size / 1024 / 1024).toFixed(2)} MB</p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); removeDocument(idx); }}
                                            className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-500/10 transition-colors"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Floating Actions */}
                <div className={clsx(
                    "sticky bottom-6 z-20 flex justify-end items-center ml-auto w-fit p-2 pr-2 pl-8 rounded-2xl border backdrop-blur-xl animate-in fade-in slide-in-from-right-4 duration-500",
                    theme !== 'dark'
                        ? 'bg-white/80 border-slate-200 shadow-[0_20px_50px_rgba(0,0,0,0.1)]'
                        : 'bg-slate-900/80 border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.3)]'
                )}>
                    <button
                        onClick={onCancel}
                        className={clsx(
                            "px-6 py-3 rounded-xl font-bold transition-all mr-2 hover:scale-105 active:scale-95",
                            theme !== 'dark' ? "hover:bg-slate-100 text-slate-500" : "hover:bg-white/10 text-slate-400"
                        )}
                    >
                        Cancelar
                    </button>
                    {/* Save-as-draft — for NEW properties and when editing an
                        existing draft. Skips full validation (title + operation
                        suffice), never publishes nor shares, and keeps the row in
                        Borradores (crm_status='draft'). Editing a draft still also
                        has the "Activar propiedad" toggle to promote it to Activas. */}
                    {(!initialData || isEditingDraft) && (
                        <button
                            onClick={() => handleSave(true)}
                            disabled={isSubmitting}
                            title="Guardar sin publicar ni compartir — queda en Borradores"
                            className={clsx(
                                "flex items-center gap-2 px-6 py-3 rounded-xl font-bold border transition-all mr-2 hover:scale-105 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100",
                                theme !== 'dark'
                                    ? "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                                    : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"
                            )}
                        >
                            {isSubmitting && submitKind === 'draft' ? (
                                <Loader2 size={18} className="animate-spin" />
                            ) : (
                                <FileText size={18} />
                            )}
                            {isSubmitting && submitKind === 'draft' ? 'Guardando...' : (initialData ? 'Actualizar Borrador' : 'Guardar Borrador')}
                        </button>
                    )}
                    <button
                        onClick={() => handleSave()}
                        disabled={isSubmitting}
                        className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-3 rounded-xl font-bold shadow-lg shadow-indigo-600/30 transition-all hover:scale-105 active:scale-95 group disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                    >
                        {isSubmitting && submitKind !== 'draft' ? (
                            <Loader2 size={20} className="animate-spin" />
                        ) : (
                            <Save size={20} className="group-hover:rotate-12 transition-transform" />
                        )}
                        {isSubmitting && submitKind !== 'draft' ? 'Guardando...' : 'Guardar Propiedad'}
                    </button>
                </div>

            </div>
        </div>
    );
};
